<template>
  <div class="relative min-h-screen w-full bg-gradient-to-br from-gray-50 to-blue-50">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav class="text-sm text-gray-600 mb-8 flex flex-wrap items-center gap-x-2 gap-y-1">
        <router-link to="/dashboard"
          class="hover:text-blue-600 transition-colors duration-200 font-medium flex items-center">
          <font-awesome-icon icon="home" class="mr-1 text-xs" />
          Dashboard
        </router-link>
        <span class="text-gray-400">/</span>
        <router-link to="/timetable-management" class="hover:text-blue-600 transition-colors duration-200 font-medium">
          Timetable Management
        </router-link>
        <span class="text-gray-400">/</span>
        <span class="text-blue-700 font-semibold">{{ pageTitle }}</span>
      </nav>

      <div v-if="loading" class="text-center py-16">
        <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
        <p class="text-gray-500 mt-4">Loading time table…</p>
      </div>

      <div v-else-if="errorMessage" class="rounded-2xl border border-red-200 bg-red-50 px-6 py-8 text-center">
        <font-awesome-icon icon="exclamation-circle" class="text-3xl text-red-500 mb-3" />
        <p class="text-red-800 font-medium">{{ errorMessage }}</p>
        <router-link to="/timetable-management"
          class="inline-flex mt-4 text-sm font-semibold text-blue-600 hover:text-blue-800">
          ← Back to Timetable Management
        </router-link>
      </div>

      <div v-else-if="semester" class="w-full space-y-6">
        <div
          class="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-8 text-white relative overflow-hidden">
          <div class="absolute inset-0 bg-black opacity-5"></div>
          <div class="relative z-10">
            <p class="text-blue-100 text-sm font-medium uppercase tracking-wide mb-1">Time Table</p>
            <h1 class="text-2xl md:text-3xl font-bold">{{ semester.name }}</h1>
            <p class="text-blue-100 mt-2 flex flex-wrap items-center gap-2">
              <font-awesome-icon icon="calendar-alt" class="opacity-90" />
              {{ formatDate(semester.start_date) }} — {{ formatDate(semester.end_date) }}
            </p>
          </div>
        </div>

        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100 bg-slate-50">
            <h2 class="text-sm font-semibold text-gray-800">Location &amp; program</h2>
          </div>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6">
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Institution</dt>
              <dd class="mt-1 text-gray-900 font-medium">{{ context.institution }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Campus</dt>
              <dd class="mt-1 text-gray-900 font-medium">{{ context.campus }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Department</dt>
              <dd class="mt-1 text-gray-900 font-medium">{{ context.department }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Program</dt>
              <dd class="mt-1 text-gray-900 font-medium">{{ context.program }}</dd>
            </div>
          </dl>
          <div class="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
            <div>
              <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</dt>
              <dd class="mt-1">
                <span :class="[
                  'inline-flex px-2.5 py-1 rounded-full text-xs font-semibold',
                  semester.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                ]">
                  {{ semester.is_active ? 'Active' : 'Inactive' }}
                </span>
              </dd>
            </div>
            <router-link to="/timetable-management"
              class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50">
              <font-awesome-icon icon="arrow-left" class="mr-2" />
              Back to management
            </router-link>
          </div>
        </div>

        <!-- Schedule configuration (optional; falls back to global default) -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div
            class="px-6 py-4 border-b border-gray-100 bg-slate-50 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 class="text-sm font-semibold text-gray-800 flex items-center">
              <font-awesome-icon icon="cog" class="mr-2 text-blue-600" />
              Schedule configuration
            </h2>
            <div class="flex items-center gap-2 shrink-0">
              <span v-if="timetableConfigLoading" class="text-xs text-gray-500">Loading…</span>
              <span v-else-if="timetablePageHasCustomConfig"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                <font-awesome-icon icon="check-circle" class="mr-1" />
                Custom
              </span>
              <span v-else-if="timetableConfigResolved"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <font-awesome-icon icon="arrow-down" class="mr-1" />
                Uses default
              </span>
            </div>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-600 mb-4 leading-relaxed">
              Optional. Set regular hours, Friday, lunch, and session limits for this time table. If you do not add a
              custom configuration, the global default from Timetable Management applies.
            </p>
            <div class="flex flex-wrap gap-2">
              <button v-if="!timetablePageHasCustomConfig" type="button" :disabled="timetableConfigLoading || !timetableConfigResolved"
                @click="openTimetableConfigModalCreate"
                class="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <font-awesome-icon icon="plus" class="mr-2" />
                Add custom configuration
              </button>
              <button v-if="timetablePageHasCustomConfig" type="button" :disabled="timetableConfigLoading"
                @click="openTimetableConfigViewModal"
                class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <font-awesome-icon icon="eye" class="mr-2" />
                View
              </button>
              <button v-if="timetablePageHasCustomConfig" type="button" :disabled="timetableConfigLoading"
                @click="openTimetableConfigModalEdit"
                class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <font-awesome-icon icon="edit" class="mr-2" />
                Edit
              </button>
              <button v-if="timetablePageHasCustomConfig" type="button" :disabled="timetableConfigLoading"
                @click="confirmRemoveTimetableConfiguration"
                class="inline-flex items-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <font-awesome-icon icon="undo" class="mr-2" />
                Use default
              </button>
            </div>
            <p v-if="timetableConfigError" class="text-sm text-red-600 mt-3">{{ timetableConfigError }}</p>
          </div>
        </div>

        <!-- Courses for this time table -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div
            class="px-4 sm:px-6 py-4 border-b border-gray-100 bg-slate-50 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center shrink-0">
              <font-awesome-icon icon="book" class="mr-2 text-blue-600" />
              Courses
            </h2>
            <div class="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto lg:flex-nowrap lg:justify-end">
              <div class="relative min-w-0 flex-1 basis-[200px] sm:basis-[220px] sm:max-w-[20rem]">
                <span
                  class="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-gray-400"
                  aria-hidden="true">
                  <font-awesome-icon icon="search" class="text-xs" />
                </span>
                <input v-model="coursesNameSearch" type="search" autocomplete="off" placeholder="Search by course name…"
                  class="w-full h-10 pl-10 pr-3 border border-gray-300 rounded-lg text-sm text-gray-800 bg-white shadow-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none"
                  @input="onCoursesNameSearchInput" />
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <label for="timetable-courses-type-filter" class="text-sm font-medium text-gray-600 whitespace-nowrap">
                  Course type
                </label>
                <select id="timetable-courses-type-filter" v-model="coursesTypeFilter"
                  @change="onCoursesTypeFilterChange"
                  class="h-10 min-w-[11rem] sm:min-w-[12rem] px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-800 bg-white shadow-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none">
                  <option value="">All</option>
                  <option value="LECTURE">Lecture/Class/Theory</option>
                  <option value="LAB">Lab</option>
                  <option value="CLINICAL">Clinical Practicum</option>
                </select>
              </div>
              <button type="button" :disabled="coursesLoading || exportingCoursesExcel || !semester"
                @click="exportCoursesExcel"
                class="inline-flex h-10 items-center justify-center shrink-0 rounded-lg border border-gray-300 bg-white px-3 sm:px-4 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Download Excel">
                <font-awesome-icon v-if="!exportingCoursesExcel" icon="file-excel"
                  class="mr-2 text-green-700 text-base" />
                <span v-else
                  class="inline-block w-4 h-4 mr-2 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></span>
                Export Excel
              </button>
              <button type="button" @click="openAddCourseModal"
                class="inline-flex h-10 items-center justify-center shrink-0 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors">
                <font-awesome-icon icon="plus" class="mr-2 text-sm" />
                Add course
              </button>
            </div>
          </div>

          <div v-if="coursesLoading" class="text-center py-12">
            <base-page-spinner type="spin" class-list="h-10"></base-page-spinner>
            <p class="text-gray-500 mt-3 text-sm">Loading courses…</p>
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full min-w-[720px] text-left text-sm border-collapse">
              <thead>
                <tr class="border-b border-gray-200 bg-gray-50">
                  <th class="px-3 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 text-center w-14">#</th>
                  <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600">Course name</th>
                  <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600">Course type</th>
                  <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 text-center min-w-[7.5rem]">Rooms
                  </th>
                  <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 text-center">Status</th>
                  <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 text-right w-40">Actions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <template v-if="coursesTotal > 0">
                  <tr v-for="(course, courseIndex) in courses" :key="'c-' + course.id" class="timetable-course-row">
                    <td class="px-3 py-3 text-center text-gray-600 tabular-nums font-medium w-14">{{
                      courseRowOrdinal(courseIndex) }}</td>
                    <td class="px-4 py-3 font-semibold text-gray-900">{{ course.name }}</td>
                    <td class="px-4 py-3 text-gray-700">
                      <span class="inline-flex items-center text-xs">
                        <font-awesome-icon icon="tag" class="mr-1 text-gray-400" />
                        {{ getCourseTypeLabel(course.course_type) }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-center align-middle">
                      <span v-if="hasCourseAssignedRooms(course)"
                        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-800"
                        title="Specific rooms selected for timetable generation">
                        <font-awesome-icon icon="door-open" class="text-[10px] opacity-80" />
                        {{ courseAssignedRoomsDisplay(course) }}
                      </span>
                      <span v-else class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                        title="Uses default room matching (campus + room type)">
                        Default
                      </span>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span :class="[
                        'inline-flex px-2 py-0.5 rounded-full text-xs font-semibold',
                        course.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                      ]">
                        {{ course.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-right whitespace-nowrap">
                      <button type="button" @click="editCourse(course)"
                        class="inline-flex p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors mr-1"
                        title="Edit">
                        <font-awesome-icon icon="edit" class="text-sm" />
                      </button>
                      <button type="button" @click="openCourseRoomsModal(course)"
                        class="inline-flex p-2 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors mr-1"
                        title="Allowed rooms for timetable generation">
                        <font-awesome-icon icon="door-open" class="text-sm" />
                      </button>
                      <button type="button" @click="deleteCourse(course.id)"
                        class="inline-flex p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete">
                        <font-awesome-icon icon="trash" class="text-sm" />
                      </button>
                    </td>
                  </tr>
                </template>
                <tr v-else class="bg-gray-50">
                  <td colspan="6" class="px-4 py-14 text-center align-middle">
                    <template v-if="coursesNameSearchTrimmed">
                      <font-awesome-icon icon="search" class="text-4xl text-gray-300 mb-3 block mx-auto" />
                      <p class="text-gray-600 font-medium">No courses match your search</p>
                      <p class="text-sm text-gray-500 mt-1 mb-6">Try a different name or clear the search box.</p>
                    </template>
                    <template v-else-if="!coursesTypeFilter">
                      <font-awesome-icon icon="book" class="text-4xl text-gray-300 mb-3 block mx-auto" />
                      <p class="text-gray-600 font-medium">No courses yet</p>
                      <p class="text-sm text-gray-500 mt-1 mb-6">Add a course with a name and type.</p>
                      <button type="button" @click="openAddCourseModal"
                        class="inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors">
                        <font-awesome-icon icon="plus" class="mr-2" />
                        Add course
                      </button>
                    </template>
                    <template v-else>
                      <font-awesome-icon icon="filter" class="text-4xl text-gray-300 mb-3 block mx-auto" />
                      <p class="text-gray-600 font-medium">No courses for this type</p>
                      <p class="text-sm text-gray-500 mt-1 mb-6">Try choosing &ldquo;All&rdquo; or add a new course
                        below.</p>
                      <button type="button" @click="openAddCourseModal"
                        class="inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors">
                        <font-awesome-icon icon="plus" class="mr-2" />
                        {{ addFirstCourseButtonLabel() }}
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="!coursesLoading && coursesTotal > 0 && coursesPaginatorInfo.lastPage > 1"
            class="px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50">
            <pagination :paginator-info="coursesPaginatorInfo" :set-page="goToCoursesPage" />
          </div>
        </div>

        <!-- Instructors for this time table -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div
            class="px-4 sm:px-6 py-4 border-b border-gray-100 bg-slate-50 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div class="min-w-0">
              <h2 class="text-lg font-semibold text-gray-900 flex items-center shrink-0">
                <font-awesome-icon icon="chalkboard-teacher" class="mr-2 text-blue-600" />
                Instructors
              </h2>
              <p class="text-sm text-gray-500 mt-1">
                Assign academic faculty from the same institution, campus, and department as this time table.
              </p>
            </div>
            <div
              class="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto lg:flex-nowrap lg:justify-end lg:shrink-0">
              <div class="relative min-w-0 flex-1 basis-[200px] sm:basis-[220px] sm:max-w-[20rem]">
                <span
                  class="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-gray-400"
                  aria-hidden="true">
                  <font-awesome-icon icon="search" class="text-xs" />
                </span>
                <input v-model="assignedInstructorsNameSearch" type="search" autocomplete="off"
                  placeholder="Search by instructor name…"
                  class="w-full h-10 pl-10 pr-3 border border-gray-300 rounded-lg text-sm text-gray-800 bg-white shadow-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none"
                  @input="onAssignedInstructorsNameSearchInput" />
              </div>
              <button type="button"
                :disabled="instructorsLoading || exportingAssignedInstructorsExcel || exportingAssignedInstructorsCourseHoursExcel || !semester"
                @click="exportAssignedInstructorsExcel"
                class="inline-flex h-10 items-center justify-center shrink-0 rounded-lg border border-gray-300 bg-white px-3 sm:px-4 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="One row per instructor (courses combined in one cell)">
                <font-awesome-icon v-if="!exportingAssignedInstructorsExcel" icon="file-excel"
                  class="mr-2 text-green-700 text-base" />
                <span v-else
                  class="inline-block w-4 h-4 mr-2 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></span>
                Export Excel
              </button>
              <button type="button"
                :disabled="instructorsLoading || exportingAssignedInstructorsExcel || exportingAssignedInstructorsCourseHoursExcel || !semester"
                @click="exportAssignedInstructorsCourseHoursExcel"
                class="inline-flex h-10 items-center justify-center shrink-0 rounded-lg border border-gray-300 bg-white px-3 sm:px-4 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="One row per instructor and course (hours per week); instructor name repeats for multiple courses">
                <font-awesome-icon v-if="!exportingAssignedInstructorsCourseHoursExcel" icon="file-excel"
                  class="mr-2 text-emerald-700 text-base" />
                <span v-else
                  class="inline-block w-4 h-4 mr-2 border-2 border-gray-300 border-t-emerald-600 rounded-full animate-spin"></span>
                Export per course
              </button>
              <button type="button" @click="openAddInstructorModal"
                class="inline-flex h-10 items-center justify-center shrink-0 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors">
                <font-awesome-icon icon="plus" class="mr-2 text-sm" />
                Add Instructor
              </button>
            </div>
          </div>

          <div v-if="instructorsLoading" class="text-center py-8">
            <base-page-spinner type="spin" class-list="h-10"></base-page-spinner>
            <p class="text-gray-500 mt-2 text-sm">Loading instructors…</p>
          </div>

          <div v-else class="min-w-0">
            <div class="px-4 sm:px-6 py-4">
              <h3 class="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Assigned</h3>
              <div v-if="assignedInstructorsTotal === 0"
                class="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-12 text-center">
                <template v-if="assignedInstructorsNameSearchTrimmed">
                  <font-awesome-icon icon="search" class="text-4xl text-gray-300 mb-3 block mx-auto" />
                  <p class="text-gray-600 font-medium">No instructors match your search</p>
                  <p class="text-sm text-gray-500 mt-1 mb-6">Try a different name or clear the search box.</p>
                </template>
                <template v-else>
                  <font-awesome-icon icon="chalkboard-teacher" class="text-4xl text-gray-300 mb-3 block mx-auto" />
                  <p class="text-gray-600 font-medium">No instructors assigned yet</p>
                  <p class="text-sm text-gray-500 mt-1 mb-6">Add faculty who can teach courses for this time table.</p>
                  <button type="button" @click="openAddInstructorModal"
                    class="inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors">
                    <font-awesome-icon icon="plus" class="mr-2" />
                    Add Instructor
                  </button>
                </template>
              </div>
              <template v-else>
                <div class="overflow-x-auto">
                  <table class="w-full min-w-[720px] text-left text-sm border-collapse">
                    <thead>
                      <tr class="border-b border-gray-200 bg-gray-50">
                        <th class="px-3 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 text-center w-14">#
                        </th>
                        <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600">Instructor</th>
                        <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 min-w-[12rem]">
                          Courses
                        </th>
                        <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 text-center min-w-[9rem]">
                          Max hrs/day</th>
                        <th class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600 text-right w-36">
                          Actions</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                      <tr v-for="(inst, instIndex) in assignedInstructors" :key="'a-' + inst.faculty_id"
                        class="timetable-instructor-row">
                        <td class="px-3 py-3 text-center text-gray-600 tabular-nums font-medium w-14 align-top">{{
                          assignedInstructorRowOrdinal(instIndex) }}</td>
                        <td class="px-4 py-3 align-top">
                          <div class="flex items-center gap-3 min-w-0">
                            <img :src="instructorProfilePictureUrl(inst.profile_picture_url)"
                              :alt="inst.name ? String(inst.name) : ''"
                              class="timetable-instructor-avatar w-11 h-11 rounded-full object-cover border border-gray-200 bg-gray-100 shrink-0"
                              @error="onInstructorAvatarError" />
                            <div class="min-w-0 flex-1">
                              <p class="font-semibold text-gray-900 leading-snug">{{ inst.name }}</p>
                              <p v-if="inst.job_title" class="text-sm text-gray-600 leading-snug">{{ inst.job_title }}
                              </p>
                              <p v-if="inst.email" class="text-xs text-gray-500 break-all leading-snug">{{ inst.email }}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td class="px-4 py-3 align-top text-xs text-gray-700 max-w-md">
                          <p v-if="instructorAssignedCourseLines(inst).length === 0" class="text-gray-400 m-0">—</p>
                          <div v-else class="space-y-1.5">
                            <p v-for="(courseLine, courseLineIdx) in instructorAssignedCourseLines(inst)"
                              :key="'acl-' + inst.faculty_id + '-' + courseLineIdx"
                              class="leading-snug text-gray-800 m-0 break-words">
                              {{ courseLine }}
                            </p>
                          </div>
                        </td>
                        <td class="px-4 py-3 align-top text-center">
                          <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
                            <input type="number" min="0" max="24" step="0.5"
                              :value="instructorMaxHoursDisplay(inst)"
                              @input="setInstructorMaxHoursDraft(inst.faculty_id, $event.target.value)"
                              class="w-full sm:w-20 h-9 px-2 border border-gray-300 rounded-lg text-sm text-gray-900 text-center tabular-nums focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none"
                              title="Maximum teaching hours per calendar day" />
                            <button type="button"
                              :disabled="savingInstructorMaxHoursFacultyId === inst.faculty_id || instructorsLoading"
                              @click="saveInstructorMaxHoursPerDay(inst)"
                              class="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 shrink-0">
                              Save
                            </button>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-right whitespace-nowrap align-top">
                          <button type="button" @click="openInstructorCoursesModal(inst)"
                            class="inline-flex p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors mr-1"
                            title="Assign courses">
                            <font-awesome-icon icon="book" class="text-sm" />
                          </button>
                          <button type="button" @click="removeTimetableInstructor(inst)"
                            class="inline-flex p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                            title="Remove from time table">
                            <font-awesome-icon icon="trash" class="text-sm" />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="assignedInstructorsTotal > 0 && assignedInstructorsPaginatorInfo.lastPage > 1"
                  class="-mx-4 sm:-mx-6 mt-4 px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <pagination :paginator-info="assignedInstructorsPaginatorInfo"
                    :set-page="goToAssignedInstructorsPage" />
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100 bg-slate-50">
            <h2 class="text-sm font-semibold text-gray-800 flex items-center">
              <font-awesome-icon icon="magic" class="mr-2 text-indigo-600" />
              Generate timetable
            </h2>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-600 mb-4 leading-relaxed">
              Build a conflict-free weekly schedule from your courses, instructor assignments (weekly hours), allowed
              rooms per course, and the schedule configuration for this semester (or the global default).
            </p>
            <div class="flex flex-wrap items-center gap-3">
              <button type="button" :disabled="!generateTimetableReady || generatingTimetable"
                @click="generateTimetableForSemester"
                class="inline-flex items-center rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <font-awesome-icon v-if="generatingTimetable" icon="spinner" spin class="mr-2" />
                <font-awesome-icon v-else icon="magic" class="mr-2" />
                {{ generatingTimetable ? 'Generating…' : 'Generate timetable' }}
              </button>
              <button type="button" :disabled="!generateTimetableReady || loadingPageTimetableDetails"
                @click="openGeneratedTimetableFullModal"
                class="inline-flex items-center rounded-lg border border-indigo-200 bg-white px-5 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <font-awesome-icon v-if="loadingPageTimetableDetails" icon="spinner" spin class="mr-2" />
                <font-awesome-icon v-else icon="calendar-alt" class="mr-2" />
                View generated timetable
              </button>
            </div>
            <p v-if="!generateTimetableReady" class="text-xs text-amber-700 mt-3">Program details must be loaded before
              generation.</p>
          </div>
        </div>
      </div>
    </div>

    <base-modal v-if="showCourseModal" width="min(28rem, calc(100vw - 2rem))" contentOverflow="visible"
      @close="closeCourseModal">
      <template #header>
        <div
          class="px-6 sm:px-8 pt-12 sm:pt-11 pb-5 pr-14 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white text-left rounded-t-2xl">
          <div class="flex gap-4">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md">
              <font-awesome-icon :icon="courseForm.id ? 'edit' : 'book'" class="text-lg" />
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <h2 class="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                {{ courseForm.id ? 'Edit course' : 'Add course' }}
              </h2>
              <p class="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {{ courseModalSubtitle }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div class="px-6 sm:px-8 py-6 sm:py-7 space-y-6">
        <div>
          <label for="timetable-course-name"
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Course name <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <span
              class="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-gray-400">
              <font-awesome-icon icon="book" class="text-sm leading-none block" />
            </span>
            <input id="timetable-course-name" v-model="courseForm.name" type="text"
              placeholder="e.g., Introduction to Nursing"
              class="w-full h-12 pl-10 pr-4 text-sm leading-normal text-gray-900 placeholder-gray-400 bg-white border rounded-xl shadow-sm transition duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :class="courseFormErrors.name ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-200 hover:border-gray-300'" />
          </div>
          <p v-if="courseFormErrors.name" class="flex items-center gap-1.5 text-red-600 text-xs mt-2 font-medium">
            <font-awesome-icon icon="exclamation-circle" class="text-xs shrink-0" />
            {{ courseFormErrors.name }}
          </p>
        </div>

        <div>
          <label for="timetable-course-type"
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Course type <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <span
              class="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-10 items-center justify-center text-gray-400">
              <font-awesome-icon icon="tag" class="text-sm leading-none block" />
            </span>
            <select id="timetable-course-type" v-model="courseForm.course_type"
              class="w-full h-12 pl-10 pr-10 text-sm leading-normal text-gray-900 bg-white border rounded-xl shadow-sm transition duration-150 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              :class="courseFormErrors.course_type ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-200 hover:border-gray-300'">
              <option value="">Select course type</option>
              <option value="LECTURE">Lecture/Class/Theory</option>
              <option value="LAB">Lab</option>
              <option value="CLINICAL">Clinical Practicum</option>
            </select>
            <span
              class="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400">
              <font-awesome-icon icon="chevron-down" class="text-xs leading-none block" />
            </span>
          </div>
          <p v-if="courseFormErrors.course_type"
            class="flex items-center gap-1.5 text-red-600 text-xs mt-2 font-medium">
            <font-awesome-icon icon="exclamation-circle" class="text-xs shrink-0" />
            {{ courseFormErrors.course_type }}
          </p>
        </div>

        <div class="rounded-xl border border-gray-200 bg-slate-50 px-4 py-4">
          <label class="flex items-start gap-3 cursor-pointer select-none">
            <input v-model="courseForm.is_active" type="checkbox"
              class="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0" />
            <span class="text-left">
              <span class="block text-sm font-semibold text-gray-900">Active</span>
              <span class="block text-xs text-gray-500 mt-0.5 leading-snug">Inactive courses are hidden from scheduling
                and
                lists.</span>
            </span>
          </label>
        </div>
      </div>

      <div class="px-6 sm:px-8 pt-0">
        <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-5 border-t border-gray-100">
          <button type="button" @click="closeCourseModal"
            class="inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition duration-150">
            Cancel
          </button>
          <button type="button" @click="courseForm.id ? submitUpdateCourse() : submitCreateCourse()"
            class="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 shadow-md shadow-blue-600/25 hover:bg-blue-700 transition duration-150">
            <font-awesome-icon :icon="courseForm.id ? 'check' : 'plus'" class="text-sm" />
            {{ courseForm.id ? 'Update course' : 'Add course' }}
          </button>
        </div>
      </div>
    </base-modal>

    <base-modal v-if="showAddInstructorModal" width="min(28rem, calc(100vw - 2rem))" contentOverflow="visible"
      @close="closeAddInstructorModal">
      <template #header>
        <div
          class="px-6 sm:px-8 pt-12 sm:pt-11 pb-5 pr-14 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white text-left rounded-t-2xl">
          <div class="flex gap-4">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md">
              <font-awesome-icon icon="user-plus" class="text-lg" />
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <h2 class="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                Add Instructor
              </h2>
              <p class="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {{ addInstructorModalSubtitle }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div
        class="timetable-add-instructor-modal-layout flex flex-col flex-1 min-h-0 w-full max-h-[min(36rem,calc(100vh-12rem))]">
        <div class="flex-1 min-h-0 overflow-visible px-6 sm:px-8 py-6 sm:py-7 space-y-6">
          <div v-if="availableInstructors.length === 0"
            class="rounded-xl border border-gray-200 bg-slate-50 px-4 py-6 text-sm text-gray-600 text-center leading-relaxed">
            Everyone who can teach in this scope is already assigned, or no academic faculty match this department.
          </div>
          <div v-else>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Select instructors
            </label>
            <div class="timetable-instructor-multiselect-wrap">
              <multiselect v-model="selectedInstructorsToAdd" :options="instructorMultiselectOptions" :multiple="true"
                :close-on-select="false" :clear-on-select="false" :searchable="true"
                placeholder="Search or choose instructors…" label="label" track-by="faculty_id" select-label=""
                selected-label="" deselect-label=""
                class="timetable-instructor-multiselect timetable-instructor-modal-multiselect" />
            </div>
            <p class="text-xs text-gray-500 mt-3 leading-relaxed">
              Only instructors whose faculty profile matches this time table&rsquo;s entity, campus, and department are
              listed.
            </p>
            <div class="mt-5 pt-5 border-t border-gray-100">
              <label for="timetable-add-inst-max-hrs-day" class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Max hours per day (each added instructor)
              </label>
              <input id="timetable-add-inst-max-hrs-day" v-model.number="addInstructorMaxHoursPerDay" type="number"
                min="0" max="24" step="0.5"
                class="w-full max-w-[12rem] h-10 px-3 border border-gray-300 rounded-lg text-sm text-gray-900 tabular-nums focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none" />
              <p class="text-xs text-gray-500 mt-2 leading-relaxed">Default 3. Limits how many hours can be scheduled on the same day when generating the timetable.</p>
            </div>
          </div>
        </div>

        <div class="flex-shrink-0 px-6 sm:px-8 pb-6 pt-0 bg-white">
          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-5 border-t border-gray-100">
            <button type="button" @click="closeAddInstructorModal"
              class="inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition duration-150">
              Cancel
            </button>
            <button type="button" :disabled="!selectedInstructorsToAdd || selectedInstructorsToAdd.length === 0"
              @click="addTimetableInstructors"
              class="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 shadow-md shadow-blue-600/25 hover:bg-blue-700 transition duration-150 disabled:opacity-40 disabled:cursor-not-allowed">
              <font-awesome-icon icon="plus" class="text-sm" />
              Add selected
            </button>
          </div>
        </div>
      </div>
    </base-modal>

    <base-modal v-if="showInstructorCoursesModal" width="min(40rem, calc(100vw - 2rem))" contentOverflow="visible"
      @close="closeInstructorCoursesModal">
      <template #header>
        <div
          class="px-6 sm:px-8 pt-12 sm:pt-11 pb-5 pr-14 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white text-left rounded-t-2xl">
          <div class="flex gap-4">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md">
              <font-awesome-icon icon="book" class="text-lg" />
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <h2 class="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                Assign courses
              </h2>
              <p class="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {{ instructorCoursesAssignmentModalSubtitle }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div
        class="timetable-add-instructor-modal-layout flex flex-col flex-1 min-h-0 w-full max-h-[min(42rem,calc(100vh-10rem))]">
        <div class="flex-1 min-h-0 overflow-y-auto px-6 sm:px-8 py-6 sm:py-7 space-y-4">
          <div v-if="loadingSemesterCoursesForAssignments" class="text-center py-6 text-sm text-gray-500">
            <base-page-spinner type="spin" class-list="h-8 mx-auto"></base-page-spinner>
            <p class="mt-2">Loading courses…</p>
          </div>
          <template v-else>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Courses for this time table
            </label>
            <div class="timetable-instructor-multiselect-wrap">
              <multiselect v-model="selectedCoursesForInstructor"
                :options="instructorCourseAssignmentMultiselectOptions" :multiple="true" :close-on-select="false"
                :clear-on-select="false" :searchable="true" placeholder="Search or choose courses…" label="label"
                track-by="id" select-label="" selected-label="" deselect-label=""
                class="timetable-instructor-multiselect timetable-instructor-assignment-multiselect" />
            </div>
            <p class="text-xs text-gray-500 leading-relaxed">
              Select courses, then set hours per week for each. Changes apply when you save.
            </p>
            <div v-if="selectedCoursesForInstructor && selectedCoursesForInstructor.length > 0" class="pt-2 border-t border-gray-100">
              <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                Hours per week (per course)
              </label>
              <ul class="space-y-3">
                <li v-for="c in selectedCoursesForInstructor" :key="'h-' + c.id"
                  class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <span class="text-sm text-gray-900 font-medium min-w-0 flex-1 leading-snug">{{ c.label }}</span>
                  <div class="flex items-center gap-2 shrink-0">
                    <label :for="'timetable-ic-hours-' + c.id" class="text-xs text-gray-500 whitespace-nowrap">Hours</label>
                    <input :id="'timetable-ic-hours-' + c.id" type="number" min="0" step="0.5"
                      :value="instructorCourseHoursDraft[String(c.id)]"
                      @input="setInstructorCourseHoursDraft(c.id, $event.target.value)"
                      class="w-24 h-9 px-2 border border-gray-300 rounded-lg text-sm text-gray-900 text-right tabular-nums focus:ring-2 focus:ring-blue-200 focus:border-blue-500 focus:outline-none" />
                  </div>
                </li>
              </ul>
            </div>
            <p v-if="instructorCoursesHoursError" class="text-sm text-red-600 mt-2">{{ instructorCoursesHoursError }}</p>
          </template>
        </div>

        <div class="flex-shrink-0 px-6 sm:px-8 pb-6 pt-0 bg-white">
          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-5 border-t border-gray-100">
            <button type="button" @click="closeInstructorCoursesModal"
              class="inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition duration-150">
              Cancel
            </button>
            <button type="button" :disabled="savingInstructorCourses || loadingSemesterCoursesForAssignments"
              @click="saveInstructorCoursesAssignment"
              class="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 shadow-md shadow-blue-600/25 hover:bg-blue-700 transition duration-150 disabled:opacity-40 disabled:cursor-not-allowed">
              <font-awesome-icon icon="check" class="text-sm" />
              Save assignments
            </button>
          </div>
        </div>
      </div>
    </base-modal>

    <base-modal v-if="showCourseRoomsModal" width="min(40rem, calc(100vw - 2rem))" contentOverflow="visible"
      @close="closeCourseRoomsModal">
      <template #header>
        <div
          class="px-6 sm:px-8 pt-12 sm:pt-11 pb-5 pr-14 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white text-left rounded-t-2xl">
          <div class="flex gap-4">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-md">
              <font-awesome-icon icon="door-open" class="text-lg" />
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <h2 class="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                Allowed rooms for this course
              </h2>
              <p class="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {{ courseRoomsModalSubtitle }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div
        class="flex flex-col flex-1 min-h-0 w-full max-h-[min(36rem,calc(100vh-10rem))]">
        <div class="flex-1 min-h-0 overflow-y-auto px-6 sm:px-8 py-6 sm:py-7 space-y-4">
          <div v-if="loadingCourseRoomsModal" class="text-center py-6 text-sm text-gray-500">
            <base-page-spinner type="spin" class-list="h-8 mx-auto"></base-page-spinner>
            <p class="mt-2">Loading rooms…</p>
          </div>
          <template v-else>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Rooms (same campus; type matches this course)
            </label>
            <div class="timetable-instructor-multiselect-wrap">
              <multiselect v-model="selectedFacilityRoomsForCourse"
                :options="facilityRoomMultiselectOptionsForCourse" :multiple="true" :close-on-select="false"
                :clear-on-select="false" :searchable="true" placeholder="Search or choose rooms…" label="label"
                track-by="id" select-label="" selected-label="" deselect-label=""
                class="timetable-instructor-multiselect timetable-course-rooms-multiselect" />
            </div>
            <p class="text-xs text-gray-500 leading-relaxed">
              Leave empty to use the default: any matching room for this campus and room type when generating the timetable.
              Select specific rooms to restrict scheduling to only those rooms.
            </p>
          </template>
        </div>

        <div class="flex-shrink-0 px-6 sm:px-8 pb-6 pt-0 bg-white">
          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-5 border-t border-gray-100">
            <button type="button" @click="closeCourseRoomsModal"
              class="inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition duration-150">
              Cancel
            </button>
            <button type="button" :disabled="savingCourseRoomsModal || loadingCourseRoomsModal"
              @click="saveCourseFacilityRoomAssignments"
              class="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-violet-600 shadow-md shadow-violet-600/25 hover:bg-violet-700 transition duration-150 disabled:opacity-40 disabled:cursor-not-allowed">
              <font-awesome-icon icon="check" class="text-sm" />
              Save
            </button>
          </div>
        </div>
      </div>
    </base-modal>

    <base-modal v-if="showTimetableConfigModal" width="min(40rem, calc(100vw - 2rem))" contentOverflow="visible"
      @close="closeTimetableConfigModal">
      <template #header>
        <div
          class="px-6 sm:px-8 pt-12 sm:pt-11 pb-5 pr-14 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white text-left rounded-t-2xl">
          <div class="flex gap-4">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md">
              <font-awesome-icon icon="cog" class="text-lg" />
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <h2 class="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                {{ timetableConfigModalEditing ? 'Edit schedule configuration' : 'Custom schedule configuration' }}
              </h2>
              <p class="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {{ semester && semester.name ? 'For "' + String(semester.name).trim() + '".' : 'For this time table.' }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div class="px-6 sm:px-8 py-6 sm:py-7 max-h-[min(70vh,calc(100vh-10rem))] overflow-y-auto">
        <configuration-form ref="timetableConfigFormRef" v-if="timetableConfigForm" :key="timetableConfigFormKey"
          v-model="timetableConfigForm" />
        <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" @click="closeTimetableConfigModal"
            class="inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" :disabled="savingTimetablePageConfig" @click="saveTimetablePageConfiguration"
            class="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 shadow-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
            <span v-if="savingTimetablePageConfig"
              class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            {{ savingTimetablePageConfig ? 'Saving…' : (timetableConfigModalEditing ? 'Save changes' : 'Save configuration') }}
          </button>
        </div>
      </div>
    </base-modal>

    <base-modal v-if="showTimetableConfigViewModal" width="min(40rem, calc(100vw - 2rem))" contentOverflow="visible"
      @close="closeTimetableConfigViewModal">
      <template #header>
        <div
          class="px-6 sm:px-8 pt-12 sm:pt-11 pb-5 pr-14 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white text-left rounded-t-2xl">
          <div class="flex gap-4">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-700 text-white shadow-md">
              <font-awesome-icon icon="eye" class="text-lg" />
            </div>
            <div class="min-w-0 flex-1 pt-0.5">
              <h2 class="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                Custom schedule configuration
              </h2>
              <p class="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {{ semester && semester.name ? 'Read-only view for "' + String(semester.name).trim() + '".' : 'Read-only view for this time table.' }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <div v-if="timetableCustomConfigForView" class="px-6 sm:px-8 py-6 sm:py-7 max-h-[min(70vh,calc(100vh-10rem))] overflow-y-auto space-y-6">
        <div class="bg-slate-50 rounded-xl p-4 border border-gray-200">
          <h4 class="font-semibold text-gray-800 mb-3 flex items-center">
            <font-awesome-icon icon="clock" class="mr-2 text-blue-600" />
            Regular Schedule (Mon–Thu)
          </h4>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500">Start</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.regular_start_time }}</dd></div>
            <div><dt class="text-gray-500">End</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.regular_end_time }}</dd></div>
          </dl>
        </div>
        <div class="bg-slate-50 rounded-xl p-4 border border-gray-200">
          <h4 class="font-semibold text-gray-800 mb-3 flex items-center">
            <font-awesome-icon icon="calendar-day" class="mr-2 text-blue-600" />
            Friday
          </h4>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500">Start</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.friday_start_time }}</dd></div>
            <div><dt class="text-gray-500">End</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.friday_end_time }}</dd></div>
          </dl>
        </div>
        <div class="bg-slate-50 rounded-xl p-4 border border-gray-200">
          <h4 class="font-semibold text-gray-800 mb-3 flex items-center">
            <font-awesome-icon icon="utensils" class="mr-2 text-blue-600" />
            Lunch Break
          </h4>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500">Start</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.lunch_break_start }}</dd></div>
            <div><dt class="text-gray-500">End</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.lunch_break_end }}</dd></div>
          </dl>
        </div>
        <div class="bg-slate-50 rounded-xl p-4 border border-gray-200">
          <h4 class="font-semibold text-gray-800 mb-3 flex items-center">
            <font-awesome-icon icon="chalkboard-teacher" class="mr-2 text-blue-600" />
            Lecture / Theory
          </h4>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500">Min hours</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              formatConfigHoursDisplay(timetableCustomConfigForView.lecture_min_hours) }}</dd></div>
            <div><dt class="text-gray-500">Max hours</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              formatConfigHoursDisplay(timetableCustomConfigForView.lecture_max_hours) }}</dd></div>
          </dl>
        </div>
        <div class="bg-slate-50 rounded-xl p-4 border border-gray-200">
          <h4 class="font-semibold text-gray-800 mb-3 flex items-center">
            <font-awesome-icon icon="flask" class="mr-2 text-blue-600" />
            Lab
          </h4>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500">Min hours</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              formatConfigHoursDisplay(timetableCustomConfigForView.lab_min_hours) }}</dd></div>
            <div><dt class="text-gray-500">Max hours</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              formatConfigHoursDisplay(timetableCustomConfigForView.lab_max_hours) }}</dd></div>
          </dl>
        </div>
        <div class="bg-slate-50 rounded-xl p-4 border border-gray-200">
          <h4 class="font-semibold text-gray-800 mb-3 flex items-center">
            <font-awesome-icon icon="user-md" class="mr-2 text-blue-600" />
            Clinical Practicum
          </h4>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><dt class="text-gray-500">Min hours</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              formatConfigHoursDisplay(timetableCustomConfigForView.clinical_min_hours) }}</dd></div>
            <div><dt class="text-gray-500">Max hours</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              formatConfigHoursDisplay(timetableCustomConfigForView.clinical_max_hours) }}</dd></div>
            <div><dt class="text-gray-500">Start</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.clinical_start_time }}</dd></div>
            <div><dt class="text-gray-500">End</dt><dd class="font-medium text-gray-900 mt-0.5">{{
              timetableCustomConfigForView.clinical_end_time }}</dd></div>
          </dl>
        </div>
        <div class="flex justify-end pt-2 border-t border-gray-100">
          <button type="button" @click="closeTimetableConfigViewModal"
            class="inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
      <div v-else class="px-6 sm:px-8 py-8 text-center">
        <p class="text-sm text-gray-600 mb-4">This configuration could not be loaded.</p>
        <button type="button" @click="closeTimetableConfigViewModal"
          class="inline-flex justify-center items-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50">
          Close
        </button>
      </div>
    </base-modal>

    <base-full-modal v-if="showGeneratedTimetableFullModal" :title="generatedTimetableModalTitle"
      @close="closeGeneratedTimetableFullModal">
      <div class="px-6 sm:px-8 py-6 max-w-[100rem] mx-auto w-full">
        <div v-if="loadingPageTimetableDetails" class="text-center py-16">
          <base-page-spinner type="spin" class-list="h-12 mx-auto" />
          <p class="text-gray-500 mt-4 text-sm">Loading timetable…</p>
        </div>
        <div v-else-if="pageTimetableDetailsSorted.length === 0" class="text-center py-16 bg-gray-50 rounded-xl border border-gray-100">
          <font-awesome-icon icon="calendar-times" class="text-4xl text-gray-300 mb-3" />
          <p class="text-gray-600 font-medium">No generated slots for this time table yet.</p>
          <p class="text-sm text-gray-500 mt-2">Generate a timetable first, then open this view again.</p>
        </div>
        <div v-else class="space-y-4">
          <p class="text-sm text-gray-600">
            Weekly grid: each cell lists sessions that overlap that time block (same layout as View Timetable).
          </p>
          <div v-if="generatedTimetableGridTimeSlots.length === 0" class="text-center py-8 bg-amber-50 rounded-xl border border-amber-100 text-sm text-amber-900">
            Could not build time rows from this data. Use the list view below.
          </div>
          <div v-else class="overflow-x-auto rounded-xl border-2 border-gray-200 shadow-sm bg-white">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gradient-to-r from-blue-600 to-blue-700">
                <tr>
                  <th
                    class="sticky left-0 z-20 px-4 py-3 text-left text-sm font-bold text-white uppercase border-r border-blue-500/80 bg-blue-600 min-w-[8.5rem] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)]">
                    <div class="flex items-center">
                      <font-awesome-icon icon="clock" class="mr-2 opacity-90" />
                      Time
                    </div>
                  </th>
                  <th v-for="day in generatedTimetableGridDays" :key="day"
                    class="px-3 py-3 text-center text-sm font-bold text-white uppercase border-r border-blue-500/80 last:border-r-0 min-w-[11rem]">
                    {{ day }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(slot, slotIdx) in generatedTimetableGridTimeSlots" :key="slot.key"
                  :class="slotIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'">
                  <td
                    class="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 border-r border-gray-200 min-w-[8.5rem] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]"
                    :class="slotIdx % 2 === 0 ? 'bg-gray-50' : 'bg-slate-100/90'">
                    {{ slot.label }}
                  </td>
                  <td v-for="day in generatedTimetableGridDays" :key="day + '-' + slot.key"
                    class="px-3 py-3 align-top border-r border-gray-200 last:border-r-0 min-w-[11rem] max-w-[16rem]">
                    <template v-for="entries in [getGeneratedTimetableGridEntries(day, slot.start, slot.end)]"
                      :key="'cell-' + day + '-' + slot.key + '-' + (entries.length ? entries.map(function (e) { return e.id; }).join('x') : '_')">
                      <div v-for="entry in entries" :key="entry.id"
                        class="bg-blue-50 border border-blue-200/80 rounded-lg p-2.5 mb-2 last:mb-0 hover:bg-blue-100/90 transition-colors shadow-sm">
                        <div class="text-xs font-semibold text-blue-950 mb-1 leading-snug">
                          <font-awesome-icon icon="book" class="mr-1 text-blue-700" />
                          {{ entry.course && entry.course.name ? entry.course.name : 'N/A' }}
                        </div>
                        <div v-if="entry.instructor && entry.instructor.name"
                          class="text-xs text-gray-700 mb-1 flex items-start gap-1">
                          <font-awesome-icon icon="user-tie" class="mt-0.5 text-gray-500 shrink-0" />
                          <span>{{ entry.instructor.name }}</span>
                        </div>
                        <div v-if="entry.room && entry.room.title" class="text-xs text-gray-600 flex items-start gap-1">
                          <font-awesome-icon icon="door-open" class="mt-0.5 text-gray-500 shrink-0" />
                          <span>{{ entry.room.title }}</span>
                        </div>
                        <div class="text-[10px] font-mono text-gray-500 mt-1.5 pt-1 border-t border-blue-100">
                          {{ formatTimetableEntryTimeRange(entry) }}
                        </div>
                      </div>
                      <span v-if="entries.length === 0" class="text-gray-300 text-sm select-none">—</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <details class="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 text-sm">
            <summary class="cursor-pointer font-semibold text-gray-800 select-none">List view (all entries)</summary>
            <div class="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-slate-800">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Day</th>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Time</th>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Course</th>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Instructor</th>
                    <th class="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wide">Room</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr v-for="entry in pageTimetableDetailsSorted" :key="'list-' + entry.id" class="hover:bg-slate-50/80">
                    <td class="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{{ entry.day }}</td>
                    <td class="px-4 py-3 text-sm text-gray-800 whitespace-nowrap font-mono">{{ formatTimetableEntryTimeRange(entry) }}</td>
                    <td class="px-4 py-3 text-sm text-gray-800">{{ entry.course && entry.course.name ? entry.course.name : '—' }}</td>
                    <td class="px-4 py-3 text-sm text-gray-800">{{ entry.instructor && entry.instructor.name ? entry.instructor.name : '—' }}</td>
                    <td class="px-4 py-3 text-sm text-gray-800">{{ entry.room && entry.room.title ? entry.room.title : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </div>
      </div>
    </base-full-modal>
  </div>
</template>

<script>
import { gql } from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import Pagination from '../../components/shared/pagination.vue';
import ConfigurationForm from './components/ConfigurationForm.vue';
import xlsx from 'xlsx/dist/xlsx.full.min.js';
import { formatDate, getDisplayEntityCode } from './utils';

export default {
  name: 'TimetablePage',
  components: {
    Pagination,
    Multiselect,
    ConfigurationForm
  },
  props: {
    token: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      semester: null,
      errorMessage: '',
      courses: [],
      coursesTotal: 0,
      coursesLoading: false,
      showCourseModal: false,
      courseForm: {
        id: null,
        name: '',
        course_type: '',
        is_active: true
      },
      courseFormErrors: {},
      coursesPage: 1,
      coursesPerPage: 10,
      coursesTypeFilter: '',
      coursesNameSearch: '',
      exportingCoursesExcel: false,
      exportingAssignedInstructorsExcel: false,
      exportingAssignedInstructorsCourseHoursExcel: false,
      assignedInstructors: [],
      assignedInstructorsTotal: 0,
      assignedInstructorsPage: 1,
      assignedInstructorsPerPage: 10,
      assignedInstructorsNameSearch: '',
      availableInstructors: [],
      instructorsLoading: false,
      selectedInstructorsToAdd: [],
      showAddInstructorModal: false,
      showInstructorCoursesModal: false,
      instructorCoursesModalInst: null,
      selectedCoursesForInstructor: [],
      instructorCourseHoursDraft: {},
      instructorCoursesHoursError: '',
      semesterCoursesForAssignments: [],
      loadingSemesterCoursesForAssignments: false,
      savingInstructorCourses: false,
      timetableConfigLoading: false,
      timetableConfigResolved: null,
      timetableConfigError: '',
      showTimetableConfigModal: false,
      showTimetableConfigViewModal: false,
      timetableConfigModalEditing: false,
      timetableConfigForm: null,
      timetableConfigFormKey: 0,
      savingTimetablePageConfig: false,
      showCourseRoomsModal: false,
      courseRoomsModalCourse: null,
      facilityRoomMultiselectOptionsForCourse: [],
      selectedFacilityRoomsForCourse: [],
      loadingCourseRoomsModal: false,
      savingCourseRoomsModal: false,
      generatingTimetable: false,
      showGeneratedTimetableFullModal: false,
      pageTimetableDetailEntries: [],
      loadingPageTimetableDetails: false,
      addInstructorMaxHoursPerDay: 3,
      instructorMaxHoursDraft: {},
      savingInstructorMaxHoursFacultyId: null
    };
  },
  computed: {
    coursesNameSearchTrimmed() {
      var s = this.coursesNameSearch && String(this.coursesNameSearch).trim();
      return s ? s : '';
    },
    assignedInstructorsNameSearchTrimmed() {
      var s = this.assignedInstructorsNameSearch && String(this.assignedInstructorsNameSearch).trim();
      return s ? s : '';
    },
    instructorMultiselectOptions() {
      var list = this.availableInstructors;
      if (!list || !Array.isArray(list)) {
        return [];
      }
      return list.map(function (i) {
        var parts = [i.name || ''];
        if (i.job_title) {
          parts.push(i.job_title);
        }
        return {
          faculty_id: i.faculty_id,
          name: i.name,
          email: i.email,
          job_title: i.job_title,
          label: parts.filter(Boolean).join(' — ')
        };
      });
    },
    coursesTotalPages() {
      var total = this.coursesTotal;
      var per = this.coursesPerPage;
      if (total === 0) {
        return 0;
      }
      return Math.ceil(total / per);
    },
    coursesPaginatorInfo() {
      var total = this.coursesTotal;
      var per = this.coursesPerPage;
      var page = this.coursesPage;
      var lastPage = this.coursesTotalPages;
      if (lastPage < 1) {
        lastPage = 1;
      }
      var firstItem = 0;
      var lastItem = 0;
      if (total > 0) {
        firstItem = (page - 1) * per + 1;
        lastItem = Math.min(page * per, total);
      }
      return {
        firstItem: firstItem,
        lastItem: lastItem,
        total: total,
        currentPage: page,
        lastPage: lastPage,
        hasMorePages: page < lastPage,
        perPage: per
      };
    },
    assignedInstructorsTotalPages() {
      var total = this.assignedInstructorsTotal;
      var per = this.assignedInstructorsPerPage;
      if (total === 0) {
        return 0;
      }
      return Math.ceil(total / per);
    },
    assignedInstructorsPaginatorInfo() {
      var total = this.assignedInstructorsTotal;
      var per = this.assignedInstructorsPerPage;
      var page = this.assignedInstructorsPage;
      var lastPage = this.assignedInstructorsTotalPages;
      if (lastPage < 1) {
        lastPage = 1;
      }
      var firstItem = 0;
      var lastItem = 0;
      if (total > 0) {
        firstItem = (page - 1) * per + 1;
        lastItem = Math.min(page * per, total);
      }
      return {
        firstItem: firstItem,
        lastItem: lastItem,
        total: total,
        currentPage: page,
        lastPage: lastPage,
        hasMorePages: page < lastPage,
        perPage: per
      };
    },
    courseModalSubtitle() {
      var sem = '';
      if (this.semester && this.semester.name) {
        sem = String(this.semester.name).trim();
      }
      if (this.courseForm.id) {
        if (sem) {
          return 'Update this course for the time table "' + sem + '".';
        }
        return 'Update the course details below.';
      }
      if (sem) {
        return 'Add a new course to "' + sem + '".';
      }
      return 'Enter the course name and type. Active courses are available for scheduling.';
    },
    addInstructorModalSubtitle() {
      var sem = '';
      if (this.semester && this.semester.name) {
        sem = String(this.semester.name).trim();
      }
      if (sem) {
        return 'Choose one or more faculty to assign to "' + sem + '".';
      }
      return 'Choose instructors to assign to this time table.';
    },
    instructorCoursesAssignmentModalSubtitle() {
      var inst = this.instructorCoursesModalInst;
      if (inst && inst.name) {
        return 'Choose courses for ' + String(inst.name).trim() + '. Only courses defined for this time table are listed.';
      }
      return 'Choose courses from this time table for this instructor.';
    },
    courseRoomsModalSubtitle() {
      var c = this.courseRoomsModalCourse;
      var sem = this.semester;
      var cn = c && c.name ? String(c.name).trim() : 'this course';
      var sn = sem && sem.name ? String(sem.name).trim() : 'this time table';
      return 'Restrict which rooms can be used for “' + cn + '” in “' + sn + '”, or leave empty for default campus and room-type matching.';
    },
    instructorCourseAssignmentMultiselectOptions() {
      var list = this.semesterCoursesForAssignments;
      if (!list || !Array.isArray(list)) {
        return [];
      }
      var self = this;
      return list.map(function (c) {
        var name = c.name || 'Course';
        var typeLabel = self.getCourseTypeLabel(c.course_type);
        return {
          id: c.id,
          name: c.name,
          course_type: c.course_type,
          is_active: c.is_active,
          label: name + ' · ' + typeLabel
        };
      });
    },
    pageTitle() {
      if (this.semester && this.semester.name) {
        return this.semester.name;
      }
      return 'Time Table';
    },
    context() {
      const dash = '—';
      const s = this.semester;
      if (!s || !s.program) {
        return {
          institution: dash,
          campus: dash,
          department: dash,
          program: dash
        };
      }
      const pd = s.program.program_department;
      const prog = s.program;
      return {
        institution: pd && pd.entity ? getDisplayEntityCode(pd.entity) : dash,
        campus: pd && pd.campus ? String(pd.campus).trim() : dash,
        department: pd && pd.name ? String(pd.name).trim() : dash,
        program: prog && prog.name ? String(prog.name).trim() : dash
      };
    },
    timetablePageHasCustomConfig() {
      var s = this.semester;
      var d = this.timetableConfigResolved;
      if (!s || !s.id || !d) {
        return false;
      }
      var sid = d.program_semester_id;
      if (sid === null || sid === undefined || sid === '') {
        return false;
      }
      return String(sid) === String(s.id);
    },
    timetableCustomConfigForView() {
      if (!this.timetablePageHasCustomConfig || !this.timetableConfigResolved) {
        return null;
      }
      return this.buildTimetableConfigFormFromResolved(true);
    },
    generateTimetableReady() {
      var s = this.semester;
      if (!s || !s.id || !s.program_id) {
        return false;
      }
      if (!s.program || !s.program.program_department || !s.program.program_department.id) {
        return false;
      }
      return true;
    },
    generatedTimetableModalTitle() {
      var sn = this.semester && this.semester.name ? String(this.semester.name) : 'Time table';
      return 'Generated timetable — ' + sn;
    },
    pageTimetableDetailsSorted() {
      var order = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 };
      var raw = this.pageTimetableDetailEntries;
      if (!raw || !raw.length) {
        return [];
      }
      var list = raw.slice();
      list.sort(function (a, b) {
        var da = order[a.day] || 99;
        var db = order[b.day] || 99;
        if (da !== db) {
          return da - db;
        }
        var sa = a.start_time ? String(a.start_time) : '';
        var sb = b.start_time ? String(b.start_time) : '';
        if (sa < sb) {
          return -1;
        }
        if (sa > sb) {
          return 1;
        }
        return 0;
      });
      return list;
    },
    generatedTimetableGridDays() {
      var order = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      var list = this.pageTimetableDetailEntries;
      if (!list || !list.length) {
        return [];
      }
      var used = {};
      for (var i = 0; i < list.length; i++) {
        var d = list[i] && list[i].day;
        if (d) {
          used[d] = true;
        }
      }
      var out = [];
      for (var j = 0; j < order.length; j++) {
        if (used[order[j]]) {
          out.push(order[j]);
        }
      }
      return out;
    },
    generatedTimetableGridTimeSlots() {
      var self = this;
      var entries = this.pageTimetableDetailsSorted;
      if (!entries || !entries.length) {
        return [];
      }
      var boundariesSet = {};
      entries.forEach(function (e) {
        var a = self.normalizeTimetableTimeForGrid(e.start_time);
        var b = self.normalizeTimetableTimeForGrid(e.end_time);
        boundariesSet[a] = true;
        boundariesSet[b] = true;
      });
      var boundaries = Object.keys(boundariesSet).sort(function (a, b) {
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      });
      var slots = [];
      for (var i = 0; i < boundaries.length - 1; i++) {
        var start = boundaries[i];
        var end = boundaries[i + 1];
        if (start >= end) {
          continue;
        }
        slots.push({
          key: start + '|' + end,
          start: start,
          end: end,
          label: self.formatTimetableEntryTimeRange({ start_time: start, end_time: end })
        });
      }
      return slots;
    }
  },
  watch: {
    token: {
      handler() {
        this.fetchSemester();
      }
    },
    selectedCoursesForInstructor: {
      handler() {
        if (!this.showInstructorCoursesModal || !this.instructorCoursesModalInst) {
          return;
        }
        this.rebuildInstructorCourseHoursDraft();
      },
      deep: true
    }
  },
  mounted() {
    this.fetchSemester();
  },
  beforeUnmount() {
    if (this._coursesSearchDebounce) {
      clearTimeout(this._coursesSearchDebounce);
      this._coursesSearchDebounce = null;
    }
    if (this._assignedInstructorsSearchDebounce) {
      clearTimeout(this._assignedInstructorsSearchDebounce);
      this._assignedInstructorsSearchDebounce = null;
    }
  },
  methods: {
    formatDate,
    instructorProfilePictureUrl(url) {
      if (!url) {
        return '/img/default-avatar.png';
      }
      if (url.indexOf('http') === 0) {
        return url;
      }
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    onInstructorAvatarError(event) {
      if (event && event.target) {
        event.target.src = '/img/default-avatar.png';
      }
    },
    getCourseTypeLabel(courseType) {
      const types = {
        LECTURE: 'Lecture/Class/Theory',
        LAB: 'Lab',
        CLINICAL: 'Clinical Practicum'
      };
      return types[courseType] || courseType || '—';
    },
    courseRowOrdinal(listIndex) {
      return (this.coursesPage - 1) * this.coursesPerPage + listIndex + 1;
    },
    assignedInstructorRowOrdinal(listIndex) {
      return (this.assignedInstructorsPage - 1) * this.assignedInstructorsPerPage + listIndex + 1;
    },
    goToAssignedInstructorsPage(p) {
      var n = parseInt(p, 10);
      if (isNaN(n) || n < 1) {
        return;
      }
      var max = this.assignedInstructorsTotalPages;
      if (max > 0 && n > max) {
        n = max;
      }
      if (n === this.assignedInstructorsPage) {
        return;
      }
      this.assignedInstructorsPage = n;
      this.loadTimetableInstructors();
    },
    addFirstCourseButtonLabel() {
      var label = this.getCourseTypeLabel(this.coursesTypeFilter);
      return 'Add First Course (' + label + ')';
    },
    onCoursesTypeFilterChange() {
      this.coursesPage = 1;
      this.loadCourses();
    },
    timetableCoursesExportSafeFileStem(name) {
      var s = name && String(name).trim() ? String(name).trim() : 'Timetable';
      return s.replace(/[/\\?%*:|"<>]/g, '-').replace(/\s+/g, ' ');
    },
    async exportCoursesExcel() {
      if (!this.semester || !this.semester.id || this.exportingCoursesExcel) {
        return;
      }
      this.exportingCoursesExcel = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query ProgramSemesterCoursesExport($program_semester_id: ID!) {
              programSemesterCoursesExport(program_semester_id: $program_semester_id) {
                error
                error_code
                message
                timetable_name
                data {
                  entity
                  campus
                  department
                  program
                  course_name
                  course_type
                }
              }
            }
          `,
          variables: { program_semester_id: this.semester.id },
          fetchPolicy: 'network-only'
        });
        var payload = response.data && response.data.programSemesterCoursesExport;
        if (!payload || payload.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: (payload && payload.message) || 'Could not export courses.'
          });
          return;
        }
        var rows = payload.data || [];
        var stem = payload.timetable_name
          ? this.timetableCoursesExportSafeFileStem(payload.timetable_name)
          : this.timetableCoursesExportSafeFileStem(this.semester.name);
        var XLSX = xlsx;
        var worksheet;
        if (rows.length === 0) {
          worksheet = XLSX.utils.aoa_to_sheet([
            ['Entity', 'Campus', 'Department', 'Program', 'Course name', 'Course type']
          ]);
        } else {
          var sheetRows = [];
          for (var i = 0; i < rows.length; i++) {
            var r = rows[i];
            sheetRows.push({
              Entity: r.entity || '',
              Campus: r.campus || '',
              Department: r.department || '',
              Program: r.program || '',
              'Course name': r.course_name || '',
              'Course type': r.course_type || ''
            });
          }
          worksheet = XLSX.utils.json_to_sheet(sheetRows);
        }
        var workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Courses');
        var fileName = stem + ' - Courses.xlsx';
        XLSX.writeFile(workbook, fileName);
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Could not export courses.'
        });
      } finally {
        this.exportingCoursesExcel = false;
      }
    },
    async exportAssignedInstructorsExcel() {
      if (!this.semester || !this.semester.id || this.exportingAssignedInstructorsExcel) {
        return;
      }
      this.exportingAssignedInstructorsExcel = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query ProgramSemesterAssignedInstructorsExport($program_semester_id: ID!) {
              programSemesterAssignedInstructorsExport(program_semester_id: $program_semester_id) {
                error
                error_code
                message
                timetable_name
                data {
                  entity
                  campus
                  department
                  program
                  instructor_name
                  courses
                  max_hours_per_day
                }
              }
            }
          `,
          variables: { program_semester_id: this.semester.id },
          fetchPolicy: 'network-only'
        });
        var payload = response.data && response.data.programSemesterAssignedInstructorsExport;
        if (!payload || payload.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: (payload && payload.message) || 'Could not export instructors.'
          });
          return;
        }
        var rows = payload.data || [];
        var stem = payload.timetable_name
          ? this.timetableCoursesExportSafeFileStem(payload.timetable_name)
          : this.timetableCoursesExportSafeFileStem(this.semester.name);
        var XLSX = xlsx;
        var worksheet;
        if (rows.length === 0) {
          worksheet = XLSX.utils.aoa_to_sheet([
            ['Entity', 'Campus', 'Department', 'Program', 'Instructor name', 'Courses', 'Max hrs/day']
          ]);
        } else {
          var sheetRows = [];
          for (var j = 0; j < rows.length; j++) {
            var ir = rows[j];
            var mhd = ir.max_hours_per_day;
            var mhdNum = typeof mhd === 'number' ? mhd : parseFloat(mhd);
            if (isNaN(mhdNum)) {
              mhdNum = 3;
            }
            sheetRows.push({
              Entity: ir.entity || '',
              Campus: ir.campus || '',
              Department: ir.department || '',
              Program: ir.program || '',
              'Instructor name': ir.instructor_name || '',
              Courses: ir.courses || '',
              'Max hrs/day': mhdNum
            });
          }
          worksheet = XLSX.utils.json_to_sheet(sheetRows);
        }
        var workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Instructors');
        var fileName = stem + ' - Instructors & Courses.xlsx';
        XLSX.writeFile(workbook, fileName);
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Could not export instructors.'
        });
      } finally {
        this.exportingAssignedInstructorsExcel = false;
      }
    },
    async exportAssignedInstructorsCourseHoursExcel() {
      if (!this.semester || !this.semester.id || this.exportingAssignedInstructorsCourseHoursExcel) {
        return;
      }
      this.exportingAssignedInstructorsCourseHoursExcel = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query ProgramSemesterAssignedInstructorCourseHoursExport($program_semester_id: ID!) {
              programSemesterAssignedInstructorCourseHoursExport(program_semester_id: $program_semester_id) {
                error
                error_code
                message
                timetable_name
                data {
                  entity
                  campus
                  department
                  program
                  instructor_name
                  course_name
                  course_type
                  hours_per_week
                }
              }
            }
          `,
          variables: { program_semester_id: this.semester.id },
          fetchPolicy: 'network-only'
        });
        var payload = response.data && response.data.programSemesterAssignedInstructorCourseHoursExport;
        if (!payload || payload.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: (payload && payload.message) || 'Could not export instructor course hours.'
          });
          return;
        }
        var rows = payload.data || [];
        var stem = payload.timetable_name
          ? this.timetableCoursesExportSafeFileStem(payload.timetable_name)
          : this.timetableCoursesExportSafeFileStem(this.semester.name);
        var XLSX = xlsx;
        var worksheet;
        if (rows.length === 0) {
          worksheet = XLSX.utils.aoa_to_sheet([
            ['Entity', 'Campus', 'Department', 'Program', 'Instructor name', 'Course name', 'Course type', 'Hours per week']
          ]);
        } else {
          var sheetRows = [];
          for (var k = 0; k < rows.length; k++) {
            var row = rows[k];
            var ct = row.course_type || '';
            var hp = row.hours_per_week;
            var hpNum = typeof hp === 'number' ? hp : parseFloat(hp);
            if (isNaN(hpNum)) {
              hpNum = 0;
            }
            sheetRows.push({
              Entity: row.entity || '',
              Campus: row.campus || '',
              Department: row.department || '',
              Program: row.program || '',
              'Instructor name': row.instructor_name || '',
              'Course name': row.course_name || '',
              'Course type': ct ? this.getCourseTypeLabel(ct) : '',
              'Hours per week': hpNum
            });
          }
          worksheet = XLSX.utils.json_to_sheet(sheetRows);
        }
        var workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Instructors by course');
        var fileName = stem + ' - Instructors by course.xlsx';
        XLSX.writeFile(workbook, fileName);
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Could not export instructor course hours.'
        });
      } finally {
        this.exportingAssignedInstructorsCourseHoursExcel = false;
      }
    },
    onCoursesNameSearchInput() {
      var self = this;
      if (self._coursesSearchDebounce) {
        clearTimeout(self._coursesSearchDebounce);
      }
      self._coursesSearchDebounce = setTimeout(function () {
        self._coursesSearchDebounce = null;
        self.coursesPage = 1;
        self.loadCourses();
      }, 350);
    },
    onAssignedInstructorsNameSearchInput() {
      var self = this;
      if (self._assignedInstructorsSearchDebounce) {
        clearTimeout(self._assignedInstructorsSearchDebounce);
      }
      self._assignedInstructorsSearchDebounce = setTimeout(function () {
        self._assignedInstructorsSearchDebounce = null;
        self.assignedInstructorsPage = 1;
        self.loadTimetableInstructors();
      }, 350);
    },
    openAddCourseModal() {
      var prefillType = '';
      var f = this.coursesTypeFilter && String(this.coursesTypeFilter).trim();
      if (f === 'LECTURE' || f === 'LAB' || f === 'CLINICAL') {
        prefillType = f;
      }
      this.courseForm = {
        id: null,
        name: '',
        course_type: prefillType,
        is_active: true
      };
      this.courseFormErrors = {};
      this.showCourseModal = true;
    },
    openAddInstructorModal() {
      this.selectedInstructorsToAdd = [];
      this.addInstructorMaxHoursPerDay = 3;
      this.showAddInstructorModal = true;
    },
    closeAddInstructorModal() {
      this.showAddInstructorModal = false;
      this.selectedInstructorsToAdd = [];
      this.addInstructorMaxHoursPerDay = 3;
    },
    instructorMaxHoursDisplay(inst) {
      if (!inst || inst.faculty_id === undefined || inst.faculty_id === null) {
        return 3;
      }
      var key = String(inst.faculty_id);
      var v = this.instructorMaxHoursDraft[key];
      if (v !== undefined && v !== null && v !== '') {
        var n = parseFloat(typeof v === 'number' ? v : String(v));
        if (!isNaN(n)) {
          return n;
        }
      }
      var mh = inst.max_hours_per_day;
      if (mh !== undefined && mh !== null && mh !== '') {
        var n2 = parseFloat(typeof mh === 'number' ? mh : String(mh));
        if (!isNaN(n2)) {
          return n2;
        }
      }
      return 3;
    },
    setInstructorMaxHoursDraft(facultyId, raw) {
      var key = facultyId !== null && facultyId !== undefined ? String(facultyId) : '';
      if (!key) {
        return;
      }
      this.instructorMaxHoursDraft = Object.assign({}, this.instructorMaxHoursDraft, { [key]: raw });
    },
    rebuildInstructorMaxHoursDraftFromList() {
      var draft = {};
      var list = this.assignedInstructors || [];
      for (var i = 0; i < list.length; i++) {
        var row = list[i];
        if (!row || row.faculty_id === undefined || row.faculty_id === null) {
          continue;
        }
        var mh = row.max_hours_per_day;
        var num = typeof mh === 'number' ? mh : parseFloat(mh);
        if (isNaN(num)) {
          num = 3;
        }
        draft[String(row.faculty_id)] = num;
      }
      this.instructorMaxHoursDraft = draft;
    },
    instructorAssignedCourseLines(inst) {
      var lines = [];
      if (!inst || !inst.courses || !inst.courses.length) {
        return lines;
      }
      for (var i = 0; i < inst.courses.length; i++) {
        var ic = inst.courses[i];
        var c = ic && ic.course;
        var label = '';
        if (c && c.name) {
          label = String(c.name).trim();
        } else if (ic && ic.course_id) {
          label = 'Course #' + String(ic.course_id);
        }
        if (!label) {
          continue;
        }
        var h = ic && ic.hours_per_week !== null && ic.hours_per_week !== undefined
          ? parseFloat(ic.hours_per_week)
          : NaN;
        if (!isNaN(h) && h > 0) {
          lines.push(label + ' (' + h + ' h/wk)');
        } else {
          lines.push(label);
        }
      }
      return lines;
    },
    async loadSemesterCoursesForAssignments() {
      if (!this.semester || !this.semester.id) {
        return;
      }
      this.loadingSemesterCoursesForAssignments = true;
      var all = [];
      try {
        var perPage = 100;
        var sid = this.semester.id;
        var maxPages = 50;
        for (var page = 1; page <= maxPages; page++) {
          const response = await this.$apollo.query({
            query: gql`
              query TimetableAssignmentCourses($program_semester_id: ID!, $page: Int!, $per_page: Int!) {
                courses(program_semester_id: $program_semester_id, page: $page, per_page: $per_page) {
                  error
                  total
                  data {
                    id
                    name
                    course_type
                    is_active
                  }
                }
              }
            `,
            variables: {
              program_semester_id: sid,
              page: page,
              per_page: perPage
            },
            fetchPolicy: 'network-only'
          });
          var payload = response.data && response.data.courses;
          if (!payload || payload.error) {
            break;
          }
          var batch = payload.data || [];
          var total = typeof payload.total === 'number' ? payload.total : 0;
          for (var b = 0; b < batch.length; b++) {
            all.push(batch[b]);
          }
          if (all.length >= total || batch.length === 0) {
            break;
          }
        }
        this.semesterCoursesForAssignments = all;
      } catch (e) {
        this.semesterCoursesForAssignments = [];
      } finally {
        this.loadingSemesterCoursesForAssignments = false;
      }
    },
    openInstructorCoursesModal(inst) {
      this.instructorCoursesModalInst = inst;
      this.showInstructorCoursesModal = true;
      this.selectedCoursesForInstructor = [];
      this.instructorCourseHoursDraft = {};
      this.instructorCoursesHoursError = '';
      var self = this;
      this.loadSemesterCoursesForAssignments().then(function () {
        self.syncSelectedCoursesForInstructorModal(inst);
      });
    },
    closeInstructorCoursesModal() {
      this.showInstructorCoursesModal = false;
      this.instructorCoursesModalInst = null;
      this.selectedCoursesForInstructor = [];
      this.instructorCourseHoursDraft = {};
      this.instructorCoursesHoursError = '';
    },
    rebuildInstructorCourseHoursDraft() {
      var selected = this.selectedCoursesForInstructor || [];
      var inst = this.instructorCoursesModalInst;
      var prev = this.instructorCourseHoursDraft || {};
      var existing = inst && inst.courses ? inst.courses : [];
      var draft = {};
      for (var i = 0; i < selected.length; i++) {
        var course = selected[i];
        if (!course || course.id === undefined || course.id === null) {
          continue;
        }
        var id = String(course.id);
        if (Object.prototype.hasOwnProperty.call(prev, id)) {
          draft[id] = prev[id];
        } else {
          var ex = null;
          for (var j = 0; j < existing.length; j++) {
            var ec = existing[j];
            if (ec && String(ec.course_id) === id) {
              ex = ec;
              break;
            }
          }
          if (ex && ex.hours_per_week !== null && ex.hours_per_week !== undefined && String(ex.hours_per_week) !== '') {
            draft[id] = String(ex.hours_per_week);
          } else {
            draft[id] = '3';
          }
        }
      }
      this.instructorCourseHoursDraft = draft;
    },
    setInstructorCourseHoursDraft(courseId, value) {
      var key = String(courseId);
      var next = Object.assign({}, this.instructorCourseHoursDraft);
      next[key] = value;
      this.instructorCourseHoursDraft = next;
      if (this.instructorCoursesHoursError) {
        this.instructorCoursesHoursError = '';
      }
    },
    syncSelectedCoursesForInstructorModal(inst) {
      var options = this.instructorCourseAssignmentMultiselectOptions;
      var byId = {};
      for (var o = 0; o < options.length; o++) {
        var op = options[o];
        byId[String(op.id)] = op;
      }
      var sel = [];
      var courses = inst && inst.courses;
      if (courses && Array.isArray(courses)) {
        for (var i = 0; i < courses.length; i++) {
          var ic = courses[i];
          var cid = ic && ic.course_id;
          if (cid && byId[String(cid)]) {
            sel.push(byId[String(cid)]);
          }
        }
      }
      this.selectedCoursesForInstructor = sel;
    },
    async saveInstructorCoursesAssignment() {
      var inst = this.instructorCoursesModalInst;
      if (!this.semester || !inst || !inst.faculty_id) {
        return;
      }
      this.instructorCoursesHoursError = '';
      var facultyId = inst.faculty_id;
      var selected = this.selectedCoursesForInstructor || [];
      var selectedIds = [];
      for (var s = 0; s < selected.length; s++) {
        selectedIds.push(String(selected[s].id));
      }
      var draft = this.instructorCourseHoursDraft || {};
      for (var valIdx = 0; valIdx < selectedIds.length; valIdx++) {
        var vid = selectedIds[valIdx];
        var rawH = draft[vid];
        var hp = parseFloat(rawH);
        if (rawH === '' || rawH === null || rawH === undefined || isNaN(hp) || hp <= 0) {
          this.instructorCoursesHoursError = 'Enter hours per week greater than 0 for each selected course.';
          return;
        }
      }
      var existing = inst.courses || [];
      var toAddCourseIds = [];
      for (var a = 0; a < selectedIds.length; a++) {
        var sid = selectedIds[a];
        var already = false;
        for (var e = 0; e < existing.length; e++) {
          if (String(existing[e].course_id) === sid) {
            already = true;
            break;
          }
        }
        if (!already) {
          toAddCourseIds.push(sid);
        }
      }
      var toRemoveIcIds = [];
      for (var r = 0; r < existing.length; r++) {
        var ex = existing[r];
        var cid = String(ex.course_id);
        var keep = false;
        for (var t = 0; t < selectedIds.length; t++) {
          if (selectedIds[t] === cid) {
            keep = true;
            break;
          }
        }
        if (!keep && ex.id) {
          toRemoveIcIds.push(ex.id);
        }
      }
      var toUpdate = [];
      for (var u = 0; u < existing.length; u++) {
        var exu = existing[u];
        if (!exu || !exu.id) {
          continue;
        }
        var removeIdx = toRemoveIcIds.indexOf(exu.id);
        if (removeIdx >= 0) {
          continue;
        }
        var cuid = String(exu.course_id);
        var stillSel = false;
        for (var ts = 0; ts < selectedIds.length; ts++) {
          if (selectedIds[ts] === cuid) {
            stillSel = true;
            break;
          }
        }
        if (!stillSel) {
          continue;
        }
        var newHu = parseFloat(draft[cuid]);
        var oldHu = parseFloat(exu.hours_per_week);
        if (isNaN(oldHu)) {
          oldHu = 0;
        }
        if (Math.abs(newHu - oldHu) > 0.0001) {
          toUpdate.push({ id: exu.id, hours_per_week: newHu });
        }
      }
      var createMutation = gql`
        mutation TimetableCreateInstructorCourse($input: CreateInstructorCourseInput!) {
          createInstructorCourse(input: $input) {
            error
            message
          }
        }
      `;
      var deleteMutation = gql`
        mutation TimetableDeleteInstructorCourse($id: ID!) {
          deleteInstructorCourse(id: $id) {
            error
            message
          }
        }
      `;
      var updateMutation = gql`
        mutation TimetableUpdateInstructorCourse($input: UpdateInstructorCourseInput!) {
          updateInstructorCourse(input: $input) {
            error
            message
          }
        }
      `;
      this.savingInstructorCourses = true;
      try {
        for (var j = 0; j < toRemoveIcIds.length; j++) {
          const response = await this.$apollo.mutate({
            mutation: deleteMutation,
            variables: { id: toRemoveIcIds[j] }
          });
          if (response.data.deleteInstructorCourse.error) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.data.deleteInstructorCourse.message || 'Could not remove course assignment.'
            });
            return;
          }
        }
        for (var i = 0; i < toAddCourseIds.length; i++) {
          var addId = toAddCourseIds[i];
          var hoursAdd = parseFloat(draft[addId]);
          const response = await this.$apollo.mutate({
            mutation: createMutation,
            variables: {
              input: {
                program_semester_id: String(this.semester.id),
                faculty_id: facultyId,
                course_id: addId,
                hours_per_week: hoursAdd
              }
            }
          });
          if (response.data.createInstructorCourse.error) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.data.createInstructorCourse.message || 'Could not assign course.'
            });
            return;
          }
        }
        for (var up = 0; up < toUpdate.length; up++) {
          var row = toUpdate[up];
          const response = await this.$apollo.mutate({
            mutation: updateMutation,
            variables: {
              input: {
                id: row.id,
                hours_per_week: row.hours_per_week
              }
            }
          });
          if (response.data.updateInstructorCourse.error) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.data.updateInstructorCourse.message || 'Could not update hours.'
            });
            return;
          }
        }
        this.closeInstructorCoursesModal();
        this.$swal.fire({
          icon: 'success',
          title: 'Course assignments updated.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2200
        });
        await this.loadTimetableInstructors();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Could not save course assignments.'
        });
      } finally {
        this.savingInstructorCourses = false;
      }
    },
    closeCourseModal() {
      this.showCourseModal = false;
      this.courseForm = {
        id: null,
        name: '',
        course_type: '',
        is_active: true
      };
      this.courseFormErrors = {};
    },
    editCourse(course) {
      this.courseForm = {
        id: course.id,
        name: course.name,
        course_type: course.course_type || '',
        is_active: course.is_active
      };
      this.courseFormErrors = {};
      this.showCourseModal = true;
    },
    formatFacilityRoomTypeLabel(roomType) {
      if (roomType === 'lab') {
        return 'Lab';
      }
      if (roomType === 'class_room') {
        return 'Classroom';
      }
      return roomType ? String(roomType) : '';
    },
    formatFacilityRoomMultiselectOption(room) {
      var t = room && room.title ? String(room.title).trim() : 'Room';
      var rt = room && room.room_type ? this.formatFacilityRoomTypeLabel(room.room_type) : '';
      return rt ? t + ' · ' + rt : t;
    },
    openCourseRoomsModal(course) {
      this.courseRoomsModalCourse = course;
      this.showCourseRoomsModal = true;
      this.facilityRoomMultiselectOptionsForCourse = [];
      this.selectedFacilityRoomsForCourse = [];
      this.loadCourseRoomsModalData();
    },
    closeCourseRoomsModal() {
      this.showCourseRoomsModal = false;
      this.courseRoomsModalCourse = null;
      this.facilityRoomMultiselectOptionsForCourse = [];
      this.selectedFacilityRoomsForCourse = [];
    },
    async loadCourseRoomsModalData() {
      if (!this.semester || !this.semester.id || !this.courseRoomsModalCourse || !this.courseRoomsModalCourse.id) {
        return;
      }
      this.loadingCourseRoomsModal = true;
      try {
        var sid = this.semester.id;
        var cid = this.courseRoomsModalCourse.id;
        const roomsPromise = this.$apollo.query({
          query: gql`
            query TimetableFacilityRooms($program_semester_id: ID!, $course_id: ID!) {
              programSemesterFacilityRooms(program_semester_id: $program_semester_id, course_id: $course_id) {
                error
                message
                data {
                  id
                  room_type
                  entity
                  campus
                  title
                  no_of_seats
                  max_seats
                }
              }
            }
          `,
          variables: { program_semester_id: sid, course_id: cid },
          fetchPolicy: 'network-only'
        });
        const idsPromise = this.$apollo.query({
          query: gql`
            query TimetableCourseFacilityRoomIds($program_semester_id: ID!, $course_id: ID!) {
              courseFacilityRoomIds(program_semester_id: $program_semester_id, course_id: $course_id) {
                error
                message
                facility_room_ids
              }
            }
          `,
          variables: { program_semester_id: sid, course_id: cid },
          fetchPolicy: 'network-only'
        });
        const results = await Promise.all([roomsPromise, idsPromise]);
        var roomsPayload = results[0].data && results[0].data.programSemesterFacilityRooms;
        var idsPayload = results[1].data && results[1].data.courseFacilityRoomIds;
        if (!roomsPayload || roomsPayload.error) {
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: (roomsPayload && roomsPayload.message) || 'Could not load rooms.'
            });
          }
          return;
        }
        if (!idsPayload || idsPayload.error) {
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: (idsPayload && idsPayload.message) || 'Could not load room selection.'
            });
          }
          return;
        }
        var roomRows = roomsPayload.data || [];
        var self = this;
        var opts = [];
        for (var r = 0; r < roomRows.length; r++) {
          var row = roomRows[r];
          opts.push({
            id: row.id,
            title: row.title,
            room_type: row.room_type,
            label: self.formatFacilityRoomMultiselectOption(row)
          });
        }
        this.facilityRoomMultiselectOptionsForCourse = opts;
        var idList = idsPayload.facility_room_ids || [];
        var idSet = {};
        for (var i = 0; i < idList.length; i++) {
          idSet[String(idList[i])] = true;
        }
        var sel = [];
        for (var j = 0; j < opts.length; j++) {
          var o = opts[j];
          if (idSet[String(o.id)]) {
            sel.push(o);
          }
        }
        this.selectedFacilityRoomsForCourse = sel;
      } catch (e) {
        if (this.$swal) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Could not load rooms.'
          });
        }
      } finally {
        this.loadingCourseRoomsModal = false;
      }
    },
    async saveCourseFacilityRoomAssignments() {
      if (!this.semester || !this.semester.id || !this.courseRoomsModalCourse || !this.courseRoomsModalCourse.id) {
        return;
      }
      this.savingCourseRoomsModal = true;
      try {
        var selected = this.selectedFacilityRoomsForCourse || [];
        var ids = [];
        for (var s = 0; s < selected.length; s++) {
          if (selected[s] && selected[s].id !== undefined && selected[s].id !== null) {
            ids.push(String(selected[s].id));
          }
        }
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation SaveCourseFacilityRoomAssignments($input: SaveCourseFacilityRoomAssignmentsInput!) {
              saveCourseFacilityRoomAssignments(input: $input) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            input: {
              program_semester_id: String(this.semester.id),
              course_id: String(this.courseRoomsModalCourse.id),
              facility_room_ids: ids
            }
          }
        });
        var res = response.data && response.data.saveCourseFacilityRoomAssignments;
        if (!res || res.error) {
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: (res && res.message) || 'Could not save room assignments.'
            });
          }
          return;
        }
        if (this.$swal) {
          this.$swal.fire({
            icon: 'success',
            title: res.message || 'Saved.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2200
          });
        }
        this.closeCourseRoomsModal();
        await this.loadCourses();
      } catch (err) {
        if (this.$swal) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Could not save room assignments.'
          });
        }
      } finally {
        this.savingCourseRoomsModal = false;
      }
    },
    hasCourseAssignedRooms(course) {
      if (!course || !course.assigned_facility_room_names) {
        return false;
      }
      return course.assigned_facility_room_names.length > 0;
    },
    courseAssignedRoomsDisplay(course) {
      if (!this.hasCourseAssignedRooms(course)) {
        return '';
      }
      return course.assigned_facility_room_names.join(', ');
    },
    validateCourseForm() {
      this.courseFormErrors = {};
      let ok = true;
      if (!this.courseForm.name || !String(this.courseForm.name).trim()) {
        this.courseFormErrors.name = 'Course name is required';
        ok = false;
      }
      if (!this.courseForm.course_type) {
        this.courseFormErrors.course_type = 'Course type is required';
        ok = false;
      }
      return ok;
    },
    async fetchSemester() {
      const t = this.token && String(this.token).trim();
      if (!t) {
        this.loading = false;
        this.errorMessage = 'Invalid time table link.';
        this.semester = null;
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      this.semester = null;
      this.courses = [];
      this.coursesTotal = 0;
      this.coursesPage = 1;
      this.coursesTypeFilter = '';
      this.coursesNameSearch = '';
      this.assignedInstructors = [];
      this.assignedInstructorsTotal = 0;
      this.assignedInstructorsPage = 1;
      this.assignedInstructorsNameSearch = '';
      this.availableInstructors = [];
      this.selectedInstructorsToAdd = [];
      this.showAddInstructorModal = false;
      this.semesterCoursesForAssignments = [];
      this.showInstructorCoursesModal = false;
      this.instructorCoursesModalInst = null;
      this.selectedCoursesForInstructor = [];
      this.instructorCourseHoursDraft = {};
      this.instructorCoursesHoursError = '';
      this.timetableConfigResolved = null;
      this.timetableConfigError = '';
      this.showTimetableConfigModal = false;
      this.showTimetableConfigViewModal = false;
      this.timetableConfigModalEditing = false;
      this.timetableConfigForm = null;
      this.showCourseRoomsModal = false;
      this.courseRoomsModalCourse = null;
      this.facilityRoomMultiselectOptionsForCourse = [];
      this.selectedFacilityRoomsForCourse = [];
      this.showGeneratedTimetableFullModal = false;
      this.pageTimetableDetailEntries = [];
      this.loadingPageTimetableDetails = false;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query ProgramSemesterByToken($token: String!) {
              programSemesterByToken(token: $token) {
                error
                error_code
                message
                data {
                  id
                  token
                  program_id
                  name
                  start_date
                  end_date
                  is_active
                  program {
                    id
                    name
                    program_department {
                      id
                      entity
                      campus
                      name
                    }
                  }
                }
              }
            }
          `,
          variables: { token: t },
          fetchPolicy: 'network-only'
        });
        const payload = response.data.programSemesterByToken;
        if (payload.error || !payload.data) {
          this.errorMessage = payload.message || 'Time table could not be loaded.';
          return;
        }
        this.semester = payload.data;
        await this.$nextTick();
        await this.loadTimetableConfiguration();
        this.loadCourses();
        this.loadTimetableInstructors();
      } catch (e) {
        this.errorMessage = 'Time table could not be loaded.';
        this.semester = null;
      } finally {
        this.loading = false;
      }
    },
    getTimetableDefaultConfigShape() {
      return {
        id: null,
        regular_start_time: '08:00',
        regular_end_time: '16:00',
        friday_start_time: '08:00',
        friday_end_time: '12:00',
        lunch_break_start: '12:00',
        lunch_break_end: '13:00',
        lecture_min_hours: 1.5,
        lecture_max_hours: 3.0,
        lab_min_hours: 6.0,
        lab_max_hours: 6.0,
        clinical_min_hours: 9.0,
        clinical_max_hours: 9.0,
        clinical_start_time: '06:30',
        clinical_end_time: '23:30'
      };
    },
    formatConfigTimeForInput(time) {
      if (time === null || time === undefined || time === '') {
        return '08:00';
      }
      var s = String(time).trim();
      if (!s) {
        return '08:00';
      }
      return s.length >= 5 ? s.substring(0, 5) : s;
    },
    ensureConfigTimeHms(t) {
      if (!t) {
        return '08:00:00';
      }
      var s = String(t);
      return s.length === 5 ? s + ':00' : s;
    },
    formatConfigHoursDisplay(n) {
      var x = parseFloat(n);
      if (isNaN(x)) {
        return '—';
      }
      return String(x) + ' h';
    },
    openTimetableConfigViewModal() {
      if (!this.timetableCustomConfigForView) {
        return;
      }
      this.showTimetableConfigViewModal = true;
    },
    closeTimetableConfigViewModal() {
      this.showTimetableConfigViewModal = false;
    },
    openGeneratedTimetableFullModal() {
      if (!this.generateTimetableReady) {
        return;
      }
      this.showGeneratedTimetableFullModal = true;
      this.loadPageTimetableDetailsForModal();
    },
    closeGeneratedTimetableFullModal() {
      this.showGeneratedTimetableFullModal = false;
      this.pageTimetableDetailEntries = [];
    },
    async loadPageTimetableDetailsForModal() {
      var sid = this.semester && this.semester.id;
      if (!sid) {
        this.pageTimetableDetailEntries = [];
        return;
      }
      this.loadingPageTimetableDetails = true;
      this.pageTimetableDetailEntries = [];
      try {
        const response = await this.$apollo.query({
          query: gql`
            query TimetablePageDetailEntries($program_semester_id: ID!) {
              timetable(program_semester_id: $program_semester_id) {
                error
                message
                data {
                  id
                  program_semester_id
                  course_id
                  course { id name }
                  instructor_course_id
                  instructor { id name }
                  room_id
                  room { id title }
                  day
                  start_time
                  end_time
                  is_active
                }
              }
            }
          `,
          variables: { program_semester_id: String(sid) },
          fetchPolicy: 'network-only'
        });
        var wrap = response.data && response.data.timetable;
        if (wrap && !wrap.error && wrap.data) {
          this.pageTimetableDetailEntries = wrap.data;
        } else {
          this.pageTimetableDetailEntries = [];
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Could not load timetable',
              text: wrap && wrap.message ? String(wrap.message) : 'Try again.'
            });
          }
        }
      } catch (e) {
        this.pageTimetableDetailEntries = [];
        if (this.$swal) {
          this.$swal.fire({
            icon: 'error',
            title: 'Could not load timetable',
            text: e && e.message ? String(e.message) : 'Try again.'
          });
        }
      } finally {
        this.loadingPageTimetableDetails = false;
      }
    },
    formatTimetableEntryTimeRange(entry) {
      var a = entry && entry.start_time ? String(entry.start_time) : '';
      var b = entry && entry.end_time ? String(entry.end_time) : '';
      function short(t) {
        if (!t) {
          return '';
        }
        var parts = t.split(':');
        if (parts.length >= 2) {
          return parts[0] + ':' + parts[1];
        }
        return t;
      }
      return short(a) + ' – ' + short(b);
    },
    normalizeTimetableTimeForGrid(t) {
      if (!t) {
        return '00:00:00';
      }
      var s = String(t).trim();
      if (s.length === 5 && s.indexOf(':') === 2) {
        return s + ':00';
      }
      return s;
    },
    getGeneratedTimetableGridEntries(day, slotStart, slotEnd) {
      var self = this;
      var list = this.pageTimetableDetailEntries;
      if (!list || !list.length) {
        return [];
      }
      var ss = self.normalizeTimetableTimeForGrid(slotStart);
      var se = self.normalizeTimetableTimeForGrid(slotEnd);
      return list.filter(function (entry) {
        if (entry.day !== day) {
          return false;
        }
        var es = self.normalizeTimetableTimeForGrid(entry.start_time);
        var ee = self.normalizeTimetableTimeForGrid(entry.end_time);
        return es < se && ee > ss;
      });
    },
    buildTimetableConfigFormFromResolved(forEdit) {
      var defaults = this.getTimetableDefaultConfigShape();
      var d = this.timetableConfigResolved;
      if (!d) {
        return defaults;
      }
      var base = {
        id: null,
        regular_start_time: this.formatConfigTimeForInput(d.regular_start_time),
        regular_end_time: this.formatConfigTimeForInput(d.regular_end_time),
        friday_start_time: this.formatConfigTimeForInput(d.friday_start_time),
        friday_end_time: this.formatConfigTimeForInput(d.friday_end_time),
        lunch_break_start: this.formatConfigTimeForInput(d.lunch_break_start),
        lunch_break_end: this.formatConfigTimeForInput(d.lunch_break_end),
        lecture_min_hours: parseFloat(d.lecture_min_hours) || 1.5,
        lecture_max_hours: parseFloat(d.lecture_max_hours) || 3.0,
        lab_min_hours: parseFloat(d.lab_min_hours) || 6.0,
        lab_max_hours: parseFloat(d.lab_max_hours) || 6.0,
        clinical_min_hours: parseFloat(d.clinical_min_hours) || 9.0,
        clinical_max_hours: parseFloat(d.clinical_max_hours) || 9.0,
        clinical_start_time: this.formatConfigTimeForInput(d.clinical_start_time),
        clinical_end_time: this.formatConfigTimeForInput(d.clinical_end_time)
      };
      if (forEdit && this.timetablePageHasCustomConfig && d.id) {
        base.id = d.id;
      }
      return base;
    },
    async loadTimetableConfiguration() {
      if (!this.semester || !this.semester.id) {
        return;
      }
      this.timetableConfigLoading = true;
      this.timetableConfigError = '';
      try {
        const response = await this.$apollo.query({
          query: gql`
            query TimetablePageConfiguration($program_semester_id: ID!) {
              timetableConfiguration(program_semester_id: $program_semester_id) {
                error
                message
                data {
                  id
                  program_semester_id
                  regular_start_time
                  regular_end_time
                  friday_start_time
                  friday_end_time
                  lunch_break_start
                  lunch_break_end
                  lecture_min_hours
                  lecture_max_hours
                  lab_min_hours
                  lab_max_hours
                  clinical_min_hours
                  clinical_max_hours
                  clinical_start_time
                  clinical_end_time
                }
              }
            }
          `,
          variables: { program_semester_id: this.semester.id },
          fetchPolicy: 'network-only'
        });
        var wrap = response.data && response.data.timetableConfiguration;
        if (!wrap || wrap.error || !wrap.data) {
          this.timetableConfigResolved = null;
          this.timetableConfigError = (wrap && wrap.message) ? String(wrap.message) : 'Could not load schedule configuration.';
          return;
        }
        this.timetableConfigResolved = wrap.data;
      } catch (e) {
        this.timetableConfigResolved = null;
        this.timetableConfigError = 'Could not load schedule configuration.';
      } finally {
        this.timetableConfigLoading = false;
      }
    },
    openTimetableConfigModalCreate() {
      this.timetableConfigModalEditing = false;
      this.timetableConfigForm = this.buildTimetableConfigFormFromResolved(false);
      this.timetableConfigFormKey += 1;
      this.showTimetableConfigModal = true;
    },
    openTimetableConfigModalEdit() {
      this.timetableConfigModalEditing = true;
      this.timetableConfigForm = this.buildTimetableConfigFormFromResolved(true);
      this.timetableConfigFormKey += 1;
      this.showTimetableConfigModal = true;
    },
    closeTimetableConfigModal() {
      this.showTimetableConfigModal = false;
      this.timetableConfigModalEditing = false;
      this.timetableConfigForm = null;
    },
    async saveTimetablePageConfiguration() {
      if (!this.semester || !this.semester.id) {
        return;
      }
      this.savingTimetablePageConfig = true;
      try {
        var c = this.timetableConfigForm;
        if (!c) {
          return;
        }
        var time = function (t) {
          return this.ensureConfigTimeHms(t);
        }.bind(this);
        if (this.timetableConfigModalEditing) {
          const response = await this.$apollo.mutate({
            mutation: gql`
              mutation SaveTimetableConfiguration($input: TimetableConfigurationInput!) {
                saveTimetableConfiguration(input: $input) {
                  error
                  message
                  data { id }
                }
              }
            `,
            variables: {
              input: {
                id: c.id,
                program_semester_id: this.semester.id,
                regular_start_time: time(c.regular_start_time),
                regular_end_time: time(c.regular_end_time),
                friday_start_time: time(c.friday_start_time),
                friday_end_time: time(c.friday_end_time),
                lunch_break_start: time(c.lunch_break_start),
                lunch_break_end: time(c.lunch_break_end),
                lecture_min_hours: c.lecture_min_hours,
                lecture_max_hours: c.lecture_max_hours,
                lab_min_hours: c.lab_min_hours,
                lab_max_hours: c.lab_max_hours,
                clinical_min_hours: c.clinical_min_hours,
                clinical_max_hours: c.clinical_max_hours,
                clinical_start_time: time(c.clinical_start_time),
                clinical_end_time: time(c.clinical_end_time)
              }
            }
          });
          var resSave = response.data.saveTimetableConfiguration;
          if (resSave.error) {
            throw new Error(resSave.message || 'Could not save.');
          }
        } else {
          const response = await this.$apollo.mutate({
            mutation: gql`
              mutation CreateTimetableConfigurationForSemester($input: CreateTimetableConfigurationForSemesterInput!) {
                createTimetableConfigurationForSemester(input: $input) {
                  error
                  message
                  data { id }
                }
              }
            `,
            variables: {
              input: {
                program_semester_id: this.semester.id,
                regular_start_time: time(c.regular_start_time),
                regular_end_time: time(c.regular_end_time),
                friday_start_time: time(c.friday_start_time),
                friday_end_time: time(c.friday_end_time),
                lunch_break_start: time(c.lunch_break_start),
                lunch_break_end: time(c.lunch_break_end),
                lecture_min_hours: c.lecture_min_hours,
                lecture_max_hours: c.lecture_max_hours,
                lab_min_hours: c.lab_min_hours,
                lab_max_hours: c.lab_max_hours,
                clinical_min_hours: c.clinical_min_hours,
                clinical_max_hours: c.clinical_max_hours,
                clinical_start_time: time(c.clinical_start_time),
                clinical_end_time: time(c.clinical_end_time)
              }
            }
          });
          var resCreate = response.data.createTimetableConfigurationForSemester;
          if (resCreate.error) {
            throw new Error(resCreate.message || 'Could not save.');
          }
        }
        if (this.$swal) {
          this.$swal.fire({
            icon: 'success',
            title: 'Schedule configuration saved',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2200
          });
        }
        this.closeTimetableConfigModal();
        await this.loadTimetableConfiguration();
      } catch (err) {
        var msg = err && err.message ? String(err.message) : 'Could not save configuration.';
        if (this.$swal) {
          this.$swal.fire({ icon: 'error', title: 'Error', text: msg });
        }
      } finally {
        this.savingTimetablePageConfig = false;
      }
    },
    async confirmRemoveTimetableConfiguration() {
      if (!this.semester || !this.semester.id) {
        return;
      }
      if (!this.$swal) {
        return;
      }
      var result = await this.$swal.fire({
        icon: 'question',
        title: 'Use default configuration?',
        text: 'This time table will follow the global default from Timetable Management.',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        confirmButtonText: 'Remove custom'
      });
      if (!result.isConfirmed) {
        return;
      }
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteTimetableConfigurationForSemester($program_semester_id: ID!) {
              deleteTimetableConfigurationForSemester(program_semester_id: $program_semester_id) {
                error
                message
              }
            }
          `,
          variables: { program_semester_id: this.semester.id }
        });
        var resDel = response.data.deleteTimetableConfigurationForSemester;
        if (resDel.error) {
          throw new Error(resDel.message || 'Could not remove.');
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Now using default configuration',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2200
        });
        await this.loadTimetableConfiguration();
      } catch (err) {
        var msg2 = err && err.message ? String(err.message) : 'Could not remove configuration.';
        this.$swal.fire({ icon: 'error', title: 'Error', text: msg2 });
      }
    },
    async generateTimetableForSemester() {
      if (!this.generateTimetableReady || this.generatingTimetable) {
        return;
      }
      if (!this.$swal) {
        return;
      }
      var s = this.semester;
      var confirmRes = await this.$swal.fire({
        icon: 'question',
        title: 'Generate timetable?',
        html: '<p class="text-left text-sm text-gray-600">Existing generated slots for this time table will be removed and replaced. Scheduling uses this semester&rsquo;s configuration (custom or default), course room allow-lists, and each instructor&rsquo;s weekly hours per course.</p>',
        showCancelButton: true,
        confirmButtonText: 'Generate',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#4f46e5'
      });
      if (!confirmRes.isConfirmed) {
        return;
      }
      this.generatingTimetable = true;
      try {
        var inputPayload = {
          program_semester_id: String(s.id)
        };
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation TimetablePageGenerateTimetable($input: GenerateTimetableInput!) {
              generateTimetable(input: $input) {
                error
                error_code
                message
                data {
                  total_entries
                  program_semester_id
                }
              }
            }
          `,
          variables: { input: inputPayload }
        });
        var gen = response.data && response.data.generateTimetable;
        if (!gen) {
          throw new Error('Unexpected response from server.');
        }
        if (gen.error) {
          throw new Error(gen.message || 'Generation failed.');
        }
        var msg = gen.message || 'Timetable generated successfully.';
        var entries = gen.data && gen.data.total_entries;
        var detail = msg;
        if (typeof entries === 'number') {
          detail = msg + ' (' + String(entries) + ' slot' + (entries === 1 ? '' : 's') + ').';
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Timetable generated',
          text: detail
        });
        if (this.showGeneratedTimetableFullModal) {
          this.loadPageTimetableDetailsForModal();
        }
      } catch (err) {
        var graphErr = err && err.graphQLErrors && err.graphQLErrors[0];
        var graphMsg = graphErr && graphErr.message ? String(graphErr.message) : '';
        var errMsg = graphMsg || (err && err.message ? String(err.message) : 'Could not generate timetable.');
        this.$swal.fire({
          icon: 'error',
          title: 'Generation failed',
          text: errMsg
        });
      } finally {
        this.generatingTimetable = false;
      }
    },
    async loadCourses() {
      if (!this.semester || !this.semester.id) {
        return;
      }
      this.coursesLoading = true;
      try {
        var courseTypeArg = this.coursesTypeFilter && String(this.coursesTypeFilter).trim()
          ? String(this.coursesTypeFilter).trim()
          : null;
        var searchArg = this.coursesNameSearch && String(this.coursesNameSearch).trim()
          ? String(this.coursesNameSearch).trim()
          : null;
        const response = await this.$apollo.query({
          query: gql`
            query TimetablePageCourses($program_semester_id: ID!, $page: Int!, $per_page: Int!, $course_type: String, $search: String) {
              courses(
                program_semester_id: $program_semester_id
                page: $page
                per_page: $per_page
                course_type: $course_type
                search: $search
              ) {
                error
                error_code
                message
                total
                data {
                  id
                  program_semester_id
                  program_id
                  name
                  course_type
                  is_active
                  assigned_facility_room_names
                }
              }
            }
          `,
          variables: {
            program_semester_id: this.semester.id,
            page: this.coursesPage,
            per_page: this.coursesPerPage,
            course_type: courseTypeArg,
            search: searchArg
          },
          fetchPolicy: 'network-only'
        });
        if (response.data.courses.error) {
          this.courses = [];
          this.coursesTotal = 0;
          this.coursesPage = 1;
          return;
        }
        var payload = response.data.courses;
        var total = payload.total;
        if (typeof total !== 'number') {
          total = 0;
        }
        this.coursesTotal = total;
        this.courses = payload.data || [];
        var lastPage = total === 0 ? 0 : Math.ceil(total / this.coursesPerPage);
        if (lastPage === 0) {
          this.coursesPage = 1;
        } else if (this.coursesPage > lastPage) {
          this.coursesPage = lastPage;
          await this.loadCourses();
          return;
        }
      } catch (e) {
        this.courses = [];
        this.coursesTotal = 0;
        this.coursesPage = 1;
      } finally {
        this.coursesLoading = false;
      }
    },
    goToCoursesPage(p) {
      var n = parseInt(p, 10);
      if (isNaN(n) || n < 1) {
        return;
      }
      var max = this.coursesTotalPages;
      if (max > 0 && n > max) {
        n = max;
      }
      if (n === this.coursesPage) {
        return;
      }
      this.coursesPage = n;
      this.loadCourses();
    },
    async loadTimetableInstructors() {
      if (!this.semester || !this.semester.id) {
        return;
      }
      this.instructorsLoading = true;
      this.selectedInstructorsToAdd = [];
      try {
        var sid = this.semester.id;
        var assignedSearchArg = this.assignedInstructorsNameSearch && String(this.assignedInstructorsNameSearch).trim()
          ? String(this.assignedInstructorsNameSearch).trim()
          : null;
        const res = await this.$apollo.query({
          query: gql`
            query TimetableInstructors(
              $program_semester_id: ID!
              $assigned_page: Int!
              $assigned_per_page: Int!
              $assigned_search: String
            ) {
              programSemesterAssignedInstructors(
                program_semester_id: $program_semester_id
                page: $assigned_page
                per_page: $assigned_per_page
                search: $assigned_search
              ) {
                error
                message
                total
                data {
                  faculty_id
                  name
                  email
                  job_title
                  profile_picture_url
                  max_hours_per_day
                  courses {
                    id
                    faculty_id
                    course_id
                    hours_per_week
                    course {
                      id
                      name
                      course_type
                    }
                  }
                }
              }
              programSemesterAvailableInstructors(program_semester_id: $program_semester_id) {
                error
                message
                data {
                  faculty_id
                  name
                  email
                  job_title
                }
              }
            }
          `,
          variables: {
            program_semester_id: sid,
            assigned_page: this.assignedInstructorsPage,
            assigned_per_page: this.assignedInstructorsPerPage,
            assigned_search: assignedSearchArg
          },
          fetchPolicy: 'network-only'
        });
        var d = res.data;
        var assigned = d.programSemesterAssignedInstructors;
        var available = d.programSemesterAvailableInstructors;
        if (assigned.error) {
          this.assignedInstructors = [];
          this.instructorMaxHoursDraft = {};
          this.assignedInstructorsTotal = 0;
          this.assignedInstructorsPage = 1;
        } else {
          this.assignedInstructors = assigned.data || [];
          this.rebuildInstructorMaxHoursDraftFromList();
          var aTotal = assigned.total;
          if (typeof aTotal !== 'number') {
            aTotal = 0;
          }
          this.assignedInstructorsTotal = aTotal;
          var aLast = aTotal === 0 ? 0 : Math.ceil(aTotal / this.assignedInstructorsPerPage);
          if (aLast === 0) {
            this.assignedInstructorsPage = 1;
          } else if (this.assignedInstructorsPage > aLast) {
            this.assignedInstructorsPage = aLast;
            await this.loadTimetableInstructors();
            return;
          }
        }
        if (available.error) {
          this.availableInstructors = [];
        } else {
          this.availableInstructors = available.data || [];
        }
      } catch (e) {
        this.assignedInstructors = [];
        this.instructorMaxHoursDraft = {};
        this.assignedInstructorsTotal = 0;
        this.assignedInstructorsPage = 1;
        this.availableInstructors = [];
      } finally {
        this.instructorsLoading = false;
      }
    },
    async addTimetableInstructors() {
      if (!this.semester || !this.selectedInstructorsToAdd || this.selectedInstructorsToAdd.length === 0) {
        return;
      }
      var mutation = gql`
        mutation TimetableAddInstructor($program_semester_id: ID!, $faculty_id: ID!, $max_hours_per_day: Float) {
          addProgramSemesterInstructor(
            program_semester_id: $program_semester_id
            faculty_id: $faculty_id
            max_hours_per_day: $max_hours_per_day
          ) {
            error
            message
          }
        }
      `;
      var semesterId = this.semester.id;
      var maxDay = this.addInstructorMaxHoursPerDay;
      var maxNum = typeof maxDay === 'number' ? maxDay : parseFloat(maxDay);
      if (isNaN(maxNum) || maxNum < 0 || maxNum > 24) {
        if (this.$swal) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Invalid value',
            text: 'Max hours per day must be between 0 and 24.'
          });
        }
        return;
      }
      var added = 0;
      var lastMessage = '';
      try {
        for (var i = 0; i < this.selectedInstructorsToAdd.length; i++) {
          var row = this.selectedInstructorsToAdd[i];
          var fid = row && row.faculty_id;
          if (!fid) {
            continue;
          }
          const response = await this.$apollo.mutate({
            mutation: mutation,
            variables: {
              program_semester_id: semesterId,
              faculty_id: fid,
              max_hours_per_day: maxNum
            }
          });
          if (response.data.addProgramSemesterInstructor.error) {
            lastMessage = response.data.addProgramSemesterInstructor.message || 'Could not add instructor.';
            break;
          }
          added += 1;
        }
        this.selectedInstructorsToAdd = [];
        await this.loadTimetableInstructors();
        if (lastMessage) {
          if (added === 0) {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: lastMessage
            });
          } else {
            this.closeAddInstructorModal();
            this.$swal.fire({
              icon: 'warning',
              title: 'Partially added',
              text: added + ' added. ' + lastMessage
            });
          }
          return;
        }
        this.closeAddInstructorModal();
        var titleText = added === 1 ? 'Instructor added.' : added + ' instructors added.';
        this.$swal.fire({
          icon: 'success',
          title: titleText,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2200
        });
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Could not add instructors.'
        });
      }
    },
    async saveInstructorMaxHoursPerDay(inst) {
      if (!this.semester || !this.semester.id || !inst || inst.faculty_id === undefined || inst.faculty_id === null) {
        return;
      }
      var key = String(inst.faculty_id);
      var raw = this.instructorMaxHoursDraft[key];
      if (raw === undefined || raw === null || raw === '') {
        var mho = inst.max_hours_per_day;
        if (mho !== undefined && mho !== null && mho !== '') {
          raw = mho;
        }
      }
      var num = typeof raw === 'number' ? raw : parseFloat(String(raw));
      if (isNaN(num) || num < 0 || num > 24) {
        if (this.$swal) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Invalid value',
            text: 'Max hours per day must be between 0 and 24.'
          });
        }
        return;
      }
      this.savingInstructorMaxHoursFacultyId = inst.faculty_id;
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation TimetableUpdateInstructorMaxHours(
              $program_semester_id: ID!
              $faculty_id: ID!
              $max_hours_per_day: Float!
            ) {
              updateProgramSemesterInstructorMaxHoursPerDay(
                program_semester_id: $program_semester_id
                faculty_id: $faculty_id
                max_hours_per_day: $max_hours_per_day
              ) {
                error
                message
              }
            }
          `,
          variables: {
            program_semester_id: this.semester.id,
            faculty_id: inst.faculty_id,
            max_hours_per_day: num
          }
        });
        var upd = response.data && response.data.updateProgramSemesterInstructorMaxHoursPerDay;
        if (!upd || upd.error) {
          throw new Error((upd && upd.message) || 'Could not save.');
        }
        var idx = this.assignedInstructors.findIndex(function (r) {
          return r && String(r.faculty_id) === key;
        });
        if (idx !== -1) {
          var prev = this.assignedInstructors[idx];
          var nextList = this.assignedInstructors.slice();
          nextList[idx] = Object.assign({}, prev, { max_hours_per_day: num });
          this.assignedInstructors = nextList;
        }
        this.instructorMaxHoursDraft = Object.assign({}, this.instructorMaxHoursDraft, { [key]: num });
        if (this.$swal) {
          this.$swal.fire({
            icon: 'success',
            title: 'Saved',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 1800
          });
        }
      } catch (e) {
        var msg = e && e.message ? String(e.message) : 'Could not save max hours per day.';
        if (this.$swal) {
          this.$swal.fire({ icon: 'error', title: 'Error', text: msg });
        }
      } finally {
        this.savingInstructorMaxHoursFacultyId = null;
      }
    },
    async removeTimetableInstructor(inst) {
      if (!this.semester || !inst) {
        return;
      }
      var facultyId = inst.faculty_id;
      if (!facultyId) {
        return;
      }
      var nameHint = inst.name && String(inst.name).trim();
      var confirmText = nameHint
        ? 'This will remove "' + nameHint + '" from this time table.'
        : 'This will remove the instructor from this time table.';
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: confirmText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, remove'
      });
      if (!result.isConfirmed) {
        return;
      }
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation TimetableRemoveInstructor($program_semester_id: ID!, $faculty_id: ID!) {
              removeProgramSemesterInstructor(program_semester_id: $program_semester_id, faculty_id: $faculty_id) {
                error
                message
              }
            }
          `,
          variables: {
            program_semester_id: this.semester.id,
            faculty_id: facultyId
          }
        });
        if (response.data.removeProgramSemesterInstructor.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.removeProgramSemesterInstructor.message
          });
          return;
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Instructor removed.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500
        });
        this.loadTimetableInstructors();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Could not remove instructor.'
        });
      }
    },
    async submitCreateCourse() {
      if (!this.validateCourseForm()) {
        return;
      }
      if (!this.semester) {
        return;
      }
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation TimetableCreateCourse($input: CreateCourseInput!) {
              createCourse(input: $input) {
                error
                error_code
                message
                data {
                  id
                  program_semester_id
                  program_id
                  name
                  course_type
                  is_active
                }
              }
            }
          `,
          variables: {
            input: {
              program_semester_id: this.semester.id,
              program_id: this.semester.program_id,
              name: String(this.courseForm.name).trim(),
              course_type: this.courseForm.course_type,
              is_active: this.courseForm.is_active !== undefined ? this.courseForm.is_active : true
            }
          }
        });
        if (response.data.createCourse.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.createCourse.message
          });
          return;
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Course created successfully.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500
        });
        this.closeCourseModal();
        this.coursesPage = 1;
        this.loadCourses();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save course. Please try again.'
        });
      }
    },
    async submitUpdateCourse() {
      if (!this.validateCourseForm()) {
        return;
      }
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation TimetableUpdateCourse($input: UpdateCourseInput!) {
              updateCourse(input: $input) {
                error
                error_code
                message
                data {
                  id
                  name
                  course_type
                  is_active
                }
              }
            }
          `,
          variables: {
            input: {
              id: this.courseForm.id,
              name: String(this.courseForm.name).trim(),
              course_type: this.courseForm.course_type,
              is_active: this.courseForm.is_active
            }
          }
        });
        if (response.data.updateCourse.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.updateCourse.message
          });
          return;
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Course updated successfully.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500
        });
        this.closeCourseModal();
        this.loadCourses();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update course. Please try again.'
        });
      }
    },
    async deleteCourse(id) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: 'This will permanently delete the course.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });
      if (!result.isConfirmed) {
        return;
      }
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation TimetableDeleteCourse($id: ID!) {
              deleteCourse(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: { id: id }
        });
        if (response.data.deleteCourse.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.deleteCourse.message
          });
          return;
        }
        this.$swal.fire({
          icon: 'success',
          title: 'Course has been deleted.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500
        });
        this.loadCourses();
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete course. Please try again.'
        });
      }
    }
  }
};
</script>

<style scoped>
.timetable-course-row {
  transition: background-color 0.15s ease;
}

.timetable-course-row:hover {
  background-color: #dbeafe;
}

.timetable-instructor-row {
  transition: background-color 0.15s ease;
}

.timetable-instructor-row:hover {
  background-color: #dbeafe;
}

.timetable-instructor-multiselect-wrap {
  position: relative;
  z-index: 30;
}

.timetable-instructor-multiselect :deep(.multiselect__tags) {
  min-height: 2.5rem;
  border-radius: 0.375rem;
  border-color: #d1d5db;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

/* In modal: many selections scroll inside the field so footer buttons stay visible; avoids clipping the options dropdown */
.timetable-instructor-modal-multiselect :deep(.multiselect__tags),
.timetable-instructor-assignment-multiselect :deep(.multiselect__tags) {
  max-height: 11rem;
  overflow-y: auto;
  overflow-x: hidden;
}

.timetable-instructor-multiselect :deep(.multiselect__input),
.timetable-instructor-multiselect :deep(.multiselect__single) {
  font-size: 0.875rem;
}

.timetable-instructor-multiselect :deep(.multiselect__tag) {
  background: #dbeafe;
  color: #1e40af;
}

.timetable-instructor-multiselect :deep(.multiselect__tag-icon::after) {
  color: #1d4ed8;
}

.timetable-instructor-multiselect :deep(.multiselect--active .multiselect__tags) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.timetable-instructor-multiselect :deep(.multiselect__content-wrapper) {
  z-index: 100;
  border-radius: 0.5rem;
}

.timetable-instructor-multiselect :deep(.multiselect) {
  z-index: 1;
}
</style>
